// Agra, "Moonlit Taj across the Yamuna": the Taj Mahal under the full moon of Sharad Purnima (late October, the night
// Agra goes out to see the marble glitter), seen from the north bank of the Yamuna at Kachhpura, the village beside
// Mehtab Bagh (the Moonlight Garden), looking south (bearing ~180°) straight across the river at the Taj's river front.
// Facing south, east is on the left: the jawab (the mosque's twin) stands left of the tomb and the mosque right, each
// on the red sandstone river terrace with an octagonal tower at its outer corner; Kachhpura's houses line the near bank
// east of the garden, so the near house is on the left.
// The Taj is not floodlit at night: it glows by moonlight alone and is the brightest thing in the frame after the moon,
// the red sandstone goes maroon-brown, and the only warm lights are on the near bank.
// Back to front: deep sky, stars, the full moon and its glow, two thin moonlit wisps -> the far bank's trees -> the
// river terrace and its octagonal towers -> the jawab (left) and the mosque (right), three white domes each on red ->
// the Taj: four minarets at the plinth's corners (the back pair inward and lower), the marble plinth, the chamfered
// tomb with its great pishtaq and stacked side arches, the four chhatris, the drum and the onion dome with its lotus
// crown and finial -> the Yamuna (the Taj's soft reflection, the moon's path, lamp streaks) -> a rowing boat mid-river
// -> the near bank: a Kachhpura house on its brick ghat (left: rooftop tank and aerial, the painted AGRA KA PETHA wall
// ad that is the billboard, a blue-washed ground floor with a lit door and window, a charpoy, kheer cooling on the
// parapet under the moon), a plank jetty with a lantern and a moored boat (right) under a harsingar tree, whose night
// flowers fall at three depths.
//
// Measured against the real tomb (a telephoto from Mehtab Bagh, the facade 57.3 m wide):
//   height from the plinth to the finial / facade width   1.08  -> drawn 109 / 101 = 1.08
//   the dome's bulb width / facade width                  0.47  -> drawn 49 / 101 = 0.49
//   the share of that height under the pishtaq's top     0.44  -> drawn 48 / 109 = 0.44
//   the front minarets' height / the tomb's height        0.70  -> drawn 77 / 109 = 0.71
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, reflect, streak, makePitch, gradeAd, FONT3 } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const CX = 240;                       // the Taj's axis (focusX)
const TIP = 35;                       // the finial's crescent (landmarkTop)
const ROOF = 105;                     // the tomb's parapet row
const BASE = 144;                     // the tomb's last row = the top of the plinth (the minarets stand here)
const DECK = 155;                     // the plinth's last row = the top of the red river terrace
const SHORE = 171;                    // the first row of the Yamuna, under the terrace wall
const FAC = { x0: 190, x1: 290 };     // the tomb's front, chamfered corners included (101 px)
const PL = { x0: 152, x1: 328 };      // the plinth's front
const MIN_F = [156, 324], MIN_B = [168, 312];   // front and back minarets (centre columns)
const MOSQ = { x0: 338, w: 63 };      // the mosque (right); the jawab mirrors it about CX
const TOWER_X = 421;                  // the octagonal river tower beyond the mosque; the jawab's is at 2 * CX - TOWER_X
const MOON = { x: 352, y: 46, r: 7 };
const TERR = 206;                     // the near ghat's front edge (people on it stand on rows 200..206)
const NEAR_WL = 226;                  // the river's edge at the foot of the near ghat (the near water mirrors the house below it)
const HOUSE_X1 = 77;                  // the Kachhpura house's right wall
const JETTY = { x0: 366, y: 205 };    // the plank jetty's deck (top row), out to the right edge
const BB = { x: 31, y: 124, w: 44, h: 18 };   // the painted wall ad on the house (the billboard's face)
const LANTERN = { x: 372, y: 182 };   // the jetty's lantern (flame)
const BOAT = { x: 286, y: 196 };      // the rowing boat mid-river (bow end, waterline)
const MOORED_GW = 216;                // the moored boat gunwale row (it bobs a pixel)

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  return { c, x, put, alpha, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
const toHex = v => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0');
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b);
  return `#${toHex(lerp(A[0], B[0], t))}${toHex(lerp(A[1], B[1], t))}${toHex(lerp(A[2], B[2], t))}`;
}
// a ramp of colours, read at v (0..1) with ordered dithering between neighbours
function ramp(pal, v, x, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(x, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
// dithered soft blob for steam
function makePuff(r, light, dark) {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
// Signs: FONT3 with wide N, K, M, W and V (FONT3's 3-wide ones read as Π and H at 1x), after the caption's own
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const signGlyph = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s].reduce((w, ch) => w + (signGlyph(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function signText(c, s, x, y, col) {
  let cx = x;
  for (const ch of s.toUpperCase()) { const g = signGlyph(ch); if (g) glyph(c, g, cx, y, col); cx += (g?.[0].length ?? 3) + 1; }
}
// a bold 4x7 face for the ad's big word
const BOLD = {
  P: ['1110', '1111', '1101', '1111', '1110', '1100', '1100'], E: ['1111', '1111', '1100', '1110', '1100', '1111', '1111'],
  T: ['1111', '1111', '0110', '0110', '0110', '0110', '0110'], H: ['1101', '1101', '1101', '1111', '1101', '1101', '1101'],
  A: ['0110', '1111', '1101', '1101', '1111', '1101', '1101'],
};

/* ================= Sky ================= */
// deep blue to a moonlit lavender haze at the horizon, lighter round the moon
const SKY = ['#08061c', '#0a0922', '#0c0d29', '#0f1130', '#121638', '#161c40', '#1a2248', '#1f2950', '#253058',
  '#2c3860', '#344168', '#3d4a70', '#485477', '#545e80', '#616888', '#6e7390'];
function renderSky() {
  return ditherFill(W, SHORE, SKY, (i, y) => {
    let v = Math.pow(y / SHORE, 1.3) * 0.97;
    const d = Math.hypot((i - MOON.x) / 1.2, y - MOON.y);
    v += 0.24 * Math.pow(Math.max(0, 1 - d / 130), 1.7);
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -3, 1.8], [1, -3, 1.4], [3, -1, 1.5], [2, 2, 1.7], [-2, 1, 1.3], [-4, -1, 1.1], [0, 4, 1]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -6 ? '#fffcf0' : '#f6f1e0';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = (dx + dy) < 0 ? '#e2dcc8' : '#d8d2be';
    if (dd > r - 0.9) col = dx + dy > 0 ? '#d4cfbc' : '#ece7d6';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(29);
  for (let i = 0; i < 120 && STARS.length < 52; i++) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 118), k = 10 + Math.floor(r() * 50), ph = r(), b = 0.25 + r() * 0.6;
    const dm = Math.hypot(x - MOON.x, y - MOON.y);
    if (dm < 36 || (x > 398 && y < 104) || (Math.abs(x - CX) < 30 && y > 30)) continue;   // moon glare, the tree, the finial
    STARS.push({ x, y, k, ph, b: b * clamp((dm - 30) / 120, 0.25, 1) });
  }
}
// two thin wisps lit from above by the moon (no blanket: the sky stays clear)
const WISPS = [{ seed: 3, y: 62, h: 12, len: 96, k: 1, x: 10 }, { seed: 11, y: 14, h: 11, len: 74, k: 2, x: 300 },
  { seed: 17, y: 32, h: 13, len: 80, k: 1, x: 170 }, { seed: 23, y: 88, h: 13, len: 90, k: 2, x: 360 },
  { seed: 29, y: 116, h: 9, len: 84, k: 1, x: 60 }, { seed: 37, y: 0, h: 12, len: 110, k: 3, x: 30 },
  { seed: 43, y: 132, h: 9, len: 70, k: 2, x: 250 }, { seed: 47, y: 47, h: 12, len: 70, k: 1, x: 120 }];
function renderWisp(def) {
  const r = rng(def.seed), puffs = [];
  for (let i = 0; i < 12; i++) {
    const u = i / 11, s = Math.sin(0.25 + u * 2.7);
    puffs.push({ x: def.x + u * def.len + (r() - 0.5) * 10, y: def.h * 0.5 + Math.sin(u * 5 + def.seed) * 1.6 + (r() - 0.5) * 1.5,
      rx: 5 + r() * 11 * s, ry: 1.2 + r() * 2.2 * s });
  }
  const p = painter(W, def.h);
  for (let y = 0; y < def.h; y++) for (let x = 0; x < W; x++) {
    let best = -9, by = 0;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const v = 1 - (dx * dx) / (q.rx * q.rx) - ((y - q.y) * (y - q.y)) / (q.ry * q.ry);
      if (v > best) { best = v; by = q.y; }
    }
    if (best <= 0 || clamp(best * 1.5, 0, 1) < bayer(x, y)) continue;
    p.put(x, y, y < by - 0.5 ? (best > 0.5 ? '#5e6c96' : '#46547e') : best > 0.5 ? '#34406a' : '#2a3560', best > 0.45 ? 200 : 130);
  }
  return p.done();
}

/* ================= Materials by moonlight ================= */
// red sandstone gone maroon-brown at night, dark to light
const SS = ['#1c0f18', '#271520', '#341b29', '#422232', '#512b3b', '#613444', '#733f4e', '#874b58'];
// the white marble of the mosque's and jawab's domes and inlay, a step below the Taj's
const DM = ['#2b2f52', '#3a3f64', '#4b5177', '#5e6489', '#72789b', '#878cad', '#9ca0be', '#b0b3cd'];
const MARB = '#8a7f9c';               // white marble inlay lines on the red walls, by moonlight
// the Taj's moonlit marble, from the deepest recess to the moon-facing shoulders
const TM = ['#2f345c', '#3c4169', '#4a5077', '#5a6086', '#6b7195', '#7e83a5', '#9295b5', '#a6a8c5', '#babbd4',
  '#cfcce0', '#e0dcec', '#eeeaf3', '#faf6f8'];
const GILT = ['#5c5244', '#7a6c56', '#9c8b68', '#c2b085'];   // the finials' gilded brass

/* paint an onion bulb: hws[i] is the half-width of row y0 + i, lit from the upper right (lum 0..1 on pal) */
function paintBulb(put, cx, y0, hws, pal, { base = 0.5, amp = 0.36, top = 0.1, rim = true, seams = 0, edge = 0 } = {}) {
  const n = hws.length;
  hws.forEach((hw, i) => {
    const y = y0 + i, v = 1 - i / Math.max(1, n - 1);       // v: 1 at the top, 0 at the bottom
    for (let dx = -hw; dx <= hw; dx++) {
      const nx = dx / (hw + 0.5);
      let l = base + amp * nx + top * v - 0.12 * Math.max(0, -nx - 0.6) / 0.4;
      if (nx > 0.55 && v > 0.35) l += 0.08;                  // the moon's highlight on the right shoulder
      if (i >= n - 2) l -= 0.08;                             // under the bulge
      if (rim && dx === -hw) l = Math.max(l - 0.05, base - amp * 0.7);
      if (seams && v > 0.12 && v < 0.92) for (let s = 1; s <= seams; s++) {
        const sx = s / (seams + 1) * 2 - 1;
        if (Math.abs(nx - sx) * (hw + 0.5) < 0.5) l -= 0.06;
      }
      if (edge && (dx === -hw || dx === hw || i === 0 || Math.abs(dx) > hws[i - 1])) l -= edge;
      put(cx + dx, y, ramp(pal, l, cx + dx, y));
    }
  });
}
// a finial: rows from its tip down, each [half-width] (0 = the rod), gilded and lit from the right
function paintFinial(put, cx, y0, hws) {
  hws.forEach((hw, i) => { for (let dx = -hw; dx <= hw; dx++) put(cx + dx, y0 + i, GILT[dx > 0 ? 3 : dx === 0 ? (hw ? 2 : 2) : 1]); });
}

/* ================= Far bank: the trees of the Taj's garden and beyond ================= */
function drawFarTrees(put) {
  const r = rng(5), crowns = [];
  for (let x = -12; x < W + 12; x += 6 + Math.floor(r() * 7)) {
    const rr = 5 + r() * 6, near = Math.abs(x - CX) < 96;
    crowns.push({ x, y: (near ? 152 : 146) - rr * 0.35 + r() * 3, r: rr });
  }
  for (let x = 0; x < W; x++) {
    let top = DECK + 1;
    for (const c of crowns) { const dx = x - c.x; if (Math.abs(dx) < c.r) top = Math.min(top, Math.round(c.y - Math.sqrt(c.r * c.r - dx * dx) * 0.8)); }
    for (let y = top; y <= DECK + 6; y++) {
      const edge = y - top;
      let col = '#111b31';
      if (edge === 0) col = hash(x, 3, 1) < 0.6 ? '#283652' : '#1e2b46';
      else if (edge === 1 && bayer(x, y) < 0.5) col = '#1a2640';
      else if (bayer(x, y) < 0.1) col = '#152038';
      put(x, y, col);
    }
  }
}

/* ================= The river terrace and its octagonal towers ================= */
const TOWER_L = 2 * CX - TOWER_X;
function drawTerrace(put) {
  for (let x = TOWER_L; x <= TOWER_X; x++) for (let y = DECK + 1; y < SHORE; y++) {
    let v = 0.46 + (hash(x, y, 7) - 0.5) * 0.06;
    if (y === DECK + 1) v = 0.8; else if (y === DECK + 2) v = 0.58;
    else if (y >= SHORE - 3) v = 0.3 - (y - SHORE + 3) * 0.06 + (hash(x >> 1, 9) < 0.3 ? -0.06 : 0);
    const u = (x - TOWER_L) % 9, ny = y - (DECK + 4);          // the blind arcade along the river wall
    if (u >= 2 && u <= 6 && ny >= 0 && ny <= 8) {
      const hw = ny === 0 ? 0 : ny === 1 ? 1 : 2, d = u - 4;
      if (Math.abs(d) <= hw) v = 0.14 + ny * 0.008;
      else if (d === hw + 1) v = 0.62;
    }
    put(x, y, ramp(SS, v, x, y));
  }
  // beyond the towers the bank drops to a strip of mud and grass
  for (let x = 0; x < W; x++) {
    if (x >= TOWER_L - 10 && x <= TOWER_X + 10) continue;
    const top = 161 + Math.round(2 * Math.sin(x * 0.21) + hash(x, 4) * 2);
    for (let y = top; y < SHORE; y++) put(x, y, y === top ? '#1e2630' : bayer(x, y) < 0.3 ? '#161c26' : '#11161f');
    if (hash(x, 5) < 0.3) put(x, top - 1, '#1a222c');
  }
}
const TOWER_ROWS = [   // [y0, y1, half-width, part], tip to foot
  [105, 107, 0, 'fin'], [108, 109, 1, 'fin'],
  [110, 110, 1, 'dome'], [111, 111, 2, 'dome'], [112, 112, 3, 'dome'], [113, 113, 4, 'dome'], [114, 116, 5, 'dome'], [117, 117, 4, 'dome'],
  [118, 118, 8, 'eave'], [119, 126, 7, 'open'], [127, 127, 9, 'eave'], [128, 128, 8, 'shade'], [129, 138, 8, 'wall2'],
  [139, 139, 10, 'eave'], [140, 140, 9, 'shade'], [141, 155, 9, 'wall1'], [156, 170, 10, 'base'],
];
function drawTower(put, cx) {
  const domeRows = TOWER_ROWS.filter(r => r[3] === 'dome'), dTop = domeRows[0][0];
  const hws = []; for (const [a, b, hw] of domeRows) for (let y = a; y <= b; y++) hws.push(hw);
  paintBulb(put, cx, dTop, hws, DM, { base: 0.42, amp: 0.34 });
  for (const [y0, y1, hw, part] of TOWER_ROWS) for (let y = y0; y <= y1; y++) for (let dx = -hw; dx <= hw; dx++) {
    const x = cx + dx, face = dx * 2 < -hw ? 0.3 : dx * 2 > hw ? 0.62 : 0.47;   // three faces of the octagon
    let v = face;
    if (part === 'fin') { put(x, y, GILT[dx > 0 ? 2 : 1]); continue; }
    if (part === 'dome') continue;
    if (part === 'eave') v = dx === -hw ? 0.5 : 0.84;
    else if (part === 'shade') v = 0.12;
    else if (part === 'open') {
      const a = Math.abs(dx), pillar = a === 7 || a === 6 || a === 2;
      if (!pillar) v = (y === y0 && a !== 4 && a !== 0) ? face : dx === 0 ? 0.2 : 0.08;
    } else if (part === 'wall2') {
      const a = Math.abs(dx);
      if ((a <= 1 && y >= 131 + (a ? 1 : 0)) || ((a === 5 || a === 6) && y >= 132)) v = 0.1;
    } else if (part === 'wall1') {
      const a = Math.abs(dx);
      if (y >= 144 + Math.max(0, a - 0) && a <= 2 || ((a === 6 || a === 7) && y >= 147)) v = 0.1;
    } else if (part === 'base') {
      v = face - 0.06 + (y >= 167 ? -0.12 : 0);
      if (Math.abs(dx) <= 2 && y >= 159 && y <= 165) v = 0.12;
    }
    put(x, y, ramp(SS, v + (hash(x, y, 3) - 0.5) * 0.05, x, y));
  }
}

/* ================= The jawab (left) and the mosque (right) ================= */
// Seen from the north bank at an angle: the long face toward the Taj in steep perspective with its pishtaq's marble
// outline, the north end three bays wide in two tiers, slim corner turrets with chhatris, and three white domes on the
// roof (the north one in front on its latticed drum, the big middle one behind it, the south one peeking further in).
const MQ_FAR = [3, 5, 6, 7, 8, 8, 9, 9, 9, 9, 9, 8, 8];
const MQ_BIG = [5, 7, 8, 9, 10, 11, 11, 12, 12, 12, 13, 13, 13, 13, 13, 13, 12, 12, 12, 12];
const MQ_FRONT = [3, 5, 6, 7, 8, 9, 9, 9, 9, 9, 9, 8, 8];
function drawMosque(put, x0, flip) {
  const w = MOSQ.w, X = u => flip ? x0 + w - 1 - u : x0 + u, P = (u, y, v) => put(X(u), y, ramp(SS, v, X(u), y));
  const PM = (u, y) => put(X(u), y, MARB);
  const sideV = flip ? 0.6 : 0.3;                            // the face toward the Taj: west on the jawab (moonlit), east on the mosque
  // domes: the south one, the big middle one, the north one on its drum
  paintBulb(put, X(26), 101, MQ_FAR, DM, { base: 0.3, amp: 0.28, edge: 0.1 });
  paintFinial(put, X(26), 96, [0, 0, 1, 1, 2]);
  paintBulb(put, X(33), 94, MQ_BIG, DM, { base: 0.4, amp: 0.34, seams: 2, edge: 0.12 });
  paintFinial(put, X(33), 86, [0, 0, 0, 0, 1, 1, 2, 3]);
  for (let y = 113; y <= 120; y++) for (let dx = -8; dx <= 8; dx++) {
    const x = X(39) + dx, nx = dx / 8.5;
    let l = 0.42 + 0.3 * nx;
    if (y >= 115 && y <= 118) l += ((x + y) & 1) ? 0.18 : -0.06;   // the lattice band
    if (y === 113) l = 0.7;
    put(x, y, ramp(DM, l, x, y));
  }
  paintBulb(put, X(39), 100, MQ_FRONT, DM, { base: 0.58, amp: 0.36, edge: 0.22 });
  paintFinial(put, X(39), 93, [0, 0, 0, 1, 1, 2, 3]);
  // the face toward the Taj, with its pishtaq
  for (let u = 0; u <= 16; u++) for (let y = 121; y <= DECK; y++) {
    let v = sideV + (hash(X(u), y, 2) - 0.5) * 0.05;
    if (y === 121) v += 0.22;
    P(u, y, v);
  }
  for (let u = 4; u <= 15; u++) for (let y = 112; y < 121; y++) P(u, y, (y === 112 ? sideV + 0.25 : sideV) + (hash(u, y, 4) - 0.5) * 0.05);
  for (let y = 112; y <= DECK; y++) { PM(4, y); PM(15, y); }
  for (let u = 4; u <= 15; u++) PM(u, 112);
  for (let y = 121; y <= DECK; y++) {
    const ny = y - 121, hw = ny === 0 ? 0 : ny === 1 ? 1 : ny <= 3 ? 2 : 3;
    for (let u = 9 - hw; u <= 10 + hw - 1; u++) P(u, y, 0.08 + ny * 0.004);
    if (ny > 0) { PM(9 - hw - 1, y); PM(10 + hw, y); } else { PM(9, y - 1); }
  }
  // the north end: two tiers of three arched bays framed in marble
  for (let u = 20; u <= 58; u++) for (let y = 120; y <= DECK; y++) {
    let v = 0.48 + (hash(X(u), y, 5) - 0.5) * 0.06;
    if (y === 120) { if ((u & 1) === 0) continue; v = 0.66; }
    else if (y === 121) v = 0.74;
    else if (y === 138) v = 0.28; else if (y === 139) v = 0.62;
    else if (y >= 152) v = y === 152 ? 0.6 : 0.36;
    P(u, y, v);
  }
  for (const c of [27, 39, 51]) for (const [f0, f1, apex] of [[124, 136, 126], [141, 151, 143]]) {
    for (let y = f0; y <= f1; y++) { PM(c - 4, y); PM(c + 4, y); }
    for (let u = c - 4; u <= c + 4; u++) PM(u, f0);
    for (let y = apex; y <= f1; y++) {
      const hw = Math.min(3, y - apex);
      for (let u = c - hw; u <= c + hw; u++) P(u, y, y - apex < 2 ? 0.06 : 0.1);
    }
  }
  // corner turrets with chhatris: between the two faces and at the outer corner
  for (const c of [18, 60]) {
    for (let y = 113; y <= DECK; y++) for (let du = -2; du <= 1; du++) P(c + du, y, (flip ? -du : du) > 0 ? 0.66 : du === -2 || du === 1 ? 0.36 : 0.52);
    for (let y = 108; y <= 112; y++) for (let du = -3; du <= 3; du++) {
      const pillar = Math.abs(du) === 3 || du === 0;
      P(c + du, y, pillar ? ((flip ? -du : du) > 0 ? 0.7 : 0.48) : 0.08);
    }
    for (let du = -4; du <= 4; du++) P(c + du, 107, 0.8);
    paintBulb(put, X(c), 102, [1, 2, 3, 3, 3], DM, { base: 0.52, amp: 0.3 });
    paintFinial(put, X(c), 98, [0, 0, 1, 1]);
  }
}

/* ================= The Taj Mahal ================= */
// Rows and widths read off a telephoto from Mehtab Bagh (the facade 57.3 m = 101 px):
//   finial 35..49 (the crescent, three gilded knobs), lotus crown 50..55, bulb 56..81 (widest 49 px two thirds down,
//   still 19 px under the crown), the drum's arcaded ring 82..87 and the drum behind the pishtaq, the pishtaq's top 96,
//   the chhatris over the side bays (domes 86..95), the parapet 104..105, the facade 106..144 (chamfer 13, bay 19,
//   pishtaq 37, bay 19, chamfer 13), the plinth 145..155, the minarets at its corners (three equal stages and a kiosk).
const BULB = [9, 11, 12, 14, 15, 16, 17, 18, 19, 20, 20, 21, 21, 22, 22, 23, 23, 23, 24, 24, 24, 24, 24, 24, 23, 23];
const CROWN = [2, 3, 5, 6, 8, 10];
const FINIAL = [[-1, 1], [-1, 0, 1], [0], [-1, 0, 1], [-1, 0, 1], [0], [-1, 0, 1], [-2, -1, 0, 1, 2], [-2, -1, 0, 1, 2], [-1, 0, 1], [0], [-1, 0, 1], [-1, 0, 1], [0], [-2, -1, 0, 1, 2]];
const CHH = [212, 268];                       // the chhatris over the side bays
const CHH_DOME = [2, 3, 5, 6, 6, 7, 7, 7, 6, 6];
const PISH = { x0: 222, x1: 258, top: 96 };   // the great pishtaq
const ARCH = [0, 3, 5, 6, 7, 8, 9, 9, 10, 10, 11, 11];   // the iwan's pointed arch from its apex (row 109)
// [from, to, half-width, part] tip to foot, for a front minaret and for a back one (further off: lower and slimmer)
const MIN_FRONT = [[68, 69, 0, 'fin'], [70, 70, 1, 'fin'], [71, 71, 1, 'dome'], [72, 72, 2, 'dome'], [73, 73, 3, 'dome'],
  [74, 75, 4, 'dome'], [76, 76, 3, 'dome'], [77, 77, 5, 'eave'], [78, 82, 4, 'kiosk'], [83, 83, 6, 'rail'], [84, 84, 6, 'floor'],
  [85, 85, 5, 'corbel'], [86, 103, 3, 'shaft'], [104, 104, 6, 'rail'], [105, 105, 6, 'floor'], [106, 106, 5, 'corbel'],
  [107, 124, 4, 'shaft'], [125, 125, 6, 'rail'], [126, 126, 6, 'floor'], [127, 127, 5, 'corbel'], [128, 143, 4, 'shaft'], [144, 144, 5, 'foot']];
const MIN_BACK = [[80, 81, 0, 'fin'], [82, 82, 1, 'dome'], [83, 83, 2, 'dome'], [84, 85, 3, 'dome'], [86, 86, 2, 'dome'],
  [87, 87, 4, 'eave'], [88, 91, 3, 'kiosk'], [92, 92, 5, 'rail'], [93, 93, 5, 'floor'], [94, 94, 4, 'corbel'], [95, 109, 2, 'shaft'],
  [110, 110, 5, 'rail'], [111, 111, 5, 'floor'], [112, 112, 4, 'corbel'], [113, 127, 3, 'shaft'], [128, 128, 5, 'rail'],
  [129, 129, 5, 'floor'], [130, 130, 4, 'corbel'], [131, 143, 3, 'shaft'], [144, 144, 4, 'foot']];
const T = (put, x, y, l) => put(x, y, ramp(TM, l, x, y));

function drawMinaret(put, cx, rows, dim) {
  const dRows = rows.filter(r => r[3] === 'dome'), hws = [];
  for (const [a, b, hw] of dRows) for (let y = a; y <= b; y++) hws.push(hw);
  paintBulb((x, y, c) => put(x, y, c), cx, dRows[0][0], hws, TM, { base: 0.66 - dim, amp: 0.3 });
  for (const [y0, y1, hw, part] of rows) for (let y = y0; y <= y1; y++) for (let dx = -hw; dx <= hw; dx++) {
    const x = cx + dx, nx = dx / (hw + 0.5);
    let l = 0.6 + 0.3 * nx - dim;
    if (part === 'dome') continue;
    if (part === 'fin') { put(x, y, GILT[dx > 0 ? 3 : 2]); continue; }
    if (part === 'eave') l = dx === -hw ? 0.55 - dim : 0.86 - dim;
    else if (part === 'kiosk') { const a = Math.abs(dx); if (a !== hw && a !== 0) l = y === y0 ? 0.5 - dim : 0.12; }
    else if (part === 'rail') l = (dx & 1) ? 0.5 - dim + 0.2 * nx : 0.74 - dim + 0.2 * nx;
    else if (part === 'floor') l = dx < -hw + 2 ? 0.6 - dim : 0.9 - dim;
    else if (part === 'corbel') l = 0.22;
    else if (part === 'shaft') { if (y % 3 === 0) l -= 0.07; if (dx === -hw) l -= 0.08; if (dx === hw) l -= 0.04; }
    else if (part === 'foot') l = 0.5 - dim;
    T(put, x, y, l);
  }
}
// a pointed-arch niche: apex row, springing after `spr` rows, down to `bot`, half-width hw, interior lum `l`
function niche(put, cx, apex, bot, hw, l, frame) {
  if (frame) {
    for (let y = apex - 2; y <= bot; y++) { T(put, cx - hw - 2, y, frame); T(put, cx + hw + 2, y, frame); }
    for (let x = cx - hw - 2; x <= cx + hw + 2; x++) T(put, x, apex - 2, frame);
  }
  for (let y = apex; y <= bot; y++) {
    const k = y - apex, w = k === 0 ? 0 : k === 1 ? Math.ceil(hw / 2) : k === 2 ? hw - 1 : hw;
    for (let dx = -w; dx <= w; dx++) T(put, cx + dx, y, l + (k < 3 ? -0.04 : 0) + (dx === -w && k > 2 ? 0.1 : 0));
    if (k > 0) { T(put, cx - w - 1, y, l + 0.42); T(put, cx + w + 1, y, l + 0.3); }
  }
}
function drawTaj(put) {
  // the back minarets, then the drum, the bulb, the crown and the finial
  for (const cx of MIN_B) drawMinaret(put, cx, MIN_BACK, 0.16);
  for (let y = 88; y <= 103; y++) for (let dx = -21; dx <= 21; dx++) {
    const x = CX + dx, nx = dx / 21.5;
    let l = 0.58 + 0.3 * nx;
    if (dx % 6 === 0) l -= 0.1;
    if (y === 92) l += 0.1;
    T(put, x, y, l);
  }
  for (let y = 82; y <= 87; y++) for (let dx = -23; dx <= 23; dx++) {
    const x = CX + dx, nx = dx / 23.5;
    let l = 0.62 + 0.3 * nx;
    if (y === 82) l += 0.16;
    else if (y === 87) l -= 0.2;
    else if (y >= 84 && y <= 86 && (dx + 30) % 3 === 0) l -= y === 84 ? 0.14 : 0.24;   // the ring's little arcade
    T(put, x, y, l);
  }
  paintBulb(put, CX, 56, BULB, TM, { base: 0.66, amp: 0.34, top: 0.08, seams: 3 });
  CROWN.forEach((hw, i) => {
    const y = 50 + i;
    for (let dx = -hw; dx <= hw; dx++) {
      const nx = dx / (hw + 0.5);
      T(put, CX + dx, y, (((dx + i) & 1) ? 0.62 : 0.84) + 0.2 * nx - (i === CROWN.length - 1 ? 0.12 : 0));
    }
  });
  FINIAL.forEach((xs, i) => { for (const dx of xs) put(CX + dx, TIP + i, GILT[dx > 0 ? 3 : dx < 0 ? 1 : 2]); });
  // the chhatris over the side bays
  for (const c of CHH) {
    paintBulb(put, c, 86, CHH_DOME, TM, { base: 0.62, amp: 0.32 });
    for (const y of [84, 85]) { put(c - 1, y, GILT[1]); put(c, y, GILT[3]); put(c + 1, y, GILT[3]); } put(c, 83, GILT[2]); put(c, 82, GILT[2]); put(c, 81, GILT[3]);
    for (let dx = -8; dx <= 8; dx++) T(put, c + dx, 96, dx === -8 ? 0.5 : 0.86);
    for (let dx = -7; dx <= 7; dx++) T(put, c + dx, 97, 0.48 + 0.2 * dx / 7);
    for (let y = 98; y <= 103; y++) for (let dx = -7; dx <= 7; dx++) {
      const a = Math.abs(dx), pillar = a >= 6 || a === 2;
      const open = !pillar && !(y === 98 && (a === 3 || a === 5 || a === 1 && false));
      T(put, c + dx, y, open ? (y === 98 && a !== 0 && a !== 4 ? 0.48 : 0.1) : 0.56 + 0.26 * dx / 7);
    }
  }
  // the facade: parapet, chamfered corners, side bays and the pishtaq
  const faceL = x => x <= 202 ? 0.46 : x >= 278 ? 0.84 : x <= 221 ? 0.66 : x >= 259 ? 0.7 : 0.68;
  for (let x = FAC.x0; x <= FAC.x1; x++) {
    if ((x & 1) === 0) T(put, x, 104, faceL(x) + 0.08);
    T(put, x, 105, faceL(x) + 0.18);
    for (let y = 106; y <= BASE; y++) {
      let l = faceL(x) + (hash(x, y, 11) - 0.5) * 0.04;
      if (y === 106) l -= 0.12;                                    // the parapet's shadow line
      if (y === 128) l -= 0.12; else if (y === 129) l += 0.1;      // the string course between the tiers
      if (x === 202 || x === 221 || x === 259 || x === 277) l -= 0.1;   // pilasters at the bay edges
      if (x === 203 || x === 278) l += 0.1;
      T(put, x, y, l);
    }
  }
  for (const [c, hw, fr] of [[196, 3, 0.56], [212, 5, 0.8], [268, 5, 0.84], [284, 3, 0.94]]) {
    niche(put, c, 112, 126, hw, 0.18, fr);                         // upper niche with its inlaid frame
    niche(put, c, 132, BASE, hw, 0.16, fr);                        // lower niche
    for (let y = 119; y <= 124; y++) for (let dx = -2; dx <= 2; dx++) if (((dx + y) & 1) === 0) T(put, c + dx, y, 0.3);   // its jali
  }
  // the pishtaq: a calligraphy band round the frame, inlaid spandrels, the iwan's pointed arch and its deep recess
  for (let x = PISH.x0; x <= PISH.x1; x++) {
    if ((x & 1) === 1) T(put, x, PISH.top - 1, 0.8);
    for (let y = PISH.top; y <= BASE; y++) {
      const ex = Math.min(x - PISH.x0, PISH.x1 - x), ey = y - PISH.top;
      let l = 0.74 + (hash(x, y, 13) - 0.5) * 0.04;
      if (ey === 0) l = 0.92;
      else if (ex === 0) l = 0.8;
      else if (ex <= 2 || ey <= 2) l = (ex === 1 || ey === 1) && ((x + y) & 1) ? 0.5 : 0.7;   // inscription
      else if (ex === 3 || ey === 3) l = 0.86;
      else if (y < 109 + 3 && ((x * 7 + y * 3) % 11 === 0)) l = 0.56;                        // floral inlay
      T(put, x, y, l);
    }
  }
  for (let y = 109; y <= BASE; y++) {
    const hw = ARCH[Math.min(ARCH.length - 1, y - 109)];
    for (let dx = -hw; dx <= hw; dx++) {
      let l = y < 119 ? 0.2 + (y - 109) * 0.01 : 0.2;              // the half-dome's soffit, then the back wall
      if (dx <= -hw + 1 && y > 112) l = 0.36;                      // its west jamb catches the moon
      if (dx >= hw - 1) l = 0.1;
      if (y >= 121 && y <= 131 && Math.abs(dx) <= 5) {            // the inner jali window
        const k = y - 121, w = k === 0 ? 1 : k === 1 ? 3 : 5;
        if (Math.abs(dx) <= w) l = ((dx + y) & 1) ? 0.22 : 0.36;
      }
      if (y >= 135 && Math.abs(dx) <= 4) { const k = y - 135; if (Math.abs(dx) <= (k === 0 ? 1 : k === 1 ? 3 : 4)) l = 0.05; }   // the door
      T(put, CX + dx, y, l);
    }
    T(put, CX - hw - 1, y, 0.96); T(put, CX + hw + 1, y, 0.86);   // the arch's marble rim
  }
  // pinnacles (guldastas) at the pishtaq's edges, the bay edges and the outer corners
  for (const [x, top] of [[PISH.x0 - 1, 88], [PISH.x1 + 1, 88], [203, 97], [277, 97], [FAC.x0, 98], [FAC.x1, 98]]) {
    for (let y = top + 3; y <= 103; y++) T(put, x, y, x > CX ? 0.82 : 0.6);
    for (const y of [top + 1, top + 2]) { T(put, x - 1, y, 0.58); T(put, x, y, 0.84); T(put, x + 1, y, 0.92); }
    T(put, x, top, 0.8);
  }
  // the plinth: a lit top edge and a row of blind arched panels
  for (let x = PL.x0; x <= PL.x1; x++) for (let y = BASE + 1; y <= DECK; y++) {
    const u = (x - PL.x0) % 8, k = y - (BASE + 4);
    let l = 0.6 + 0.06 * (x - PL.x0) / (PL.x1 - PL.x0);
    if (y === BASE + 1) l = 0.88; else if (y === BASE + 2) l = 0.5;
    else if (y === DECK) l = 0.4;
    else if (u === 0) l = 0.46;
    else if (k >= 0 && k <= 5 && u >= 2 && u <= 6) {
      const hw = k === 0 ? 0 : k === 1 ? 1 : 2;
      if (Math.abs(u - 4) <= hw) l = 0.3; else if (u - 4 === hw + 1) l = 0.74;
    }
    T(put, x, y, l);
  }
  for (const cx of MIN_F) drawMinaret(put, cx, MIN_FRONT, 0);
}

/* ================= The Yamuna ================= */
const WATER = ['#0a1229', '#0d1630', '#101b38', '#142040', '#182548', '#1d2b50'];
function renderWater() {
  return ditherFill(W, H - SHORE, WATER, (i, y) => 0.95 - Math.pow(y / (H - SHORE), 0.7) * 0.85
    + 0.18 * Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, y / 40)));
}
// the moonlit lap along the foot of the terrace wall
function drawLap(t) {
  ctx.fillStyle = '#4c5a86';
  for (let x = TOWER_L; x <= TOWER_X; x++) if (hash(x >> 1, step(t, 480), 21) < 0.45) ctx.fillRect(x, SHORE, 1, 1);
  ctx.fillStyle = '#2c3a62';
  for (let x = 0; x < W; x++) if (hash(x >> 2, step(t, 240), 22) < 0.3) ctx.fillRect(x, SHORE + 1, 1, 1);
}
// short moonlit ripples drifting on the river, brighter round the moon's path
const RIPPLES = [];
{ const r = rng(77); for (let i = 0; i < 190; i++) RIPPLES.push({ x: r() * W, y: SHORE + 3 + Math.pow(r(), 1.3) * (H - SHORE - 6), w: 2 + Math.floor(r() * 6), k: 1 + Math.floor(r() * 3), ph: r() }); }
// moon glitter: points of light winking on the ripples round the moon's path
function drawGlitter(t, vl) {
  const s = step(t, 480);
  ctx.fillStyle = '#e8e6f4';
  for (let i = 0; i < 260; i++) {
    const y = SHORE + 3 + Math.floor(hash(i, 1, 91) * 80), spread = 18 + (y - SHORE) * 1.1;
    const x = Math.round(MOON.x + (hash(i, 2, 91) - 0.5) * 2 * spread);
    if (hash(i, s, 92) < 0.16 * (1 - 0.7 * vl)) { ctx.globalAlpha = 0.5 + 0.5 * hash(i, s, 93); ctx.fillRect(x, y, 1 + (hash(i, 3, 91) < 0.3 ? 1 : 0), 1); }
  }
  ctx.globalAlpha = 1;
}
function drawRipples(t) {
  for (const p of RIPPLES) {
    const x = Math.round(frac(p.x / W - p.k * t / LOOP) * (W + 12)) - 6, dm = Math.abs(x - MOON.x);
    const a = (0.18 + 0.4 * Math.max(0, 1 - dm / 60)) * (0.5 + 0.5 * wave(t, p.k * 12, p.ph));
    if (a < 0.08) continue;
    ctx.globalAlpha = a; ctx.fillStyle = dm < 24 ? '#c8cce8' : '#5a6a98';
    ctx.fillRect(x, Math.round(p.y), p.w + Math.round((p.y - SHORE) / 30), 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= The Kachhpura house (the cozy corner, left) ================= */
// A two-storey village house on the brick ghat: a black water tank, a TV aerial and a washing line on the roof; a
// rose lime-washed upper floor with a barred window and the painted wall ad; a concrete sunshade; a blue lime-washed
// ground floor with a lit door (a printed curtain), a barred window and a tube light; the brick terrace in front with
// a charpoy, the parapet where the kheer cools under the full moon (Sharad Purnima), and the steps down to the river.
const ROSE = ['#2a1e2c', '#382636', '#46303f', '#553a48', '#654552', '#76525c', '#8a6066'];
const BLUE = ['#141e32', '#1a2640', '#20304c', '#283a58', '#304664', '#3c5470', '#4a627c'];
const BRICK = ['#1a1016', '#24151c', '#2f1b22', '#3b2228', '#482a2e', '#583436'];
const HOUSE_BASE = 199;               // the house's last row: the terrace runs in front of it, rows 200..206
function renderHouse() {
  const p = painter(W, H), put = p.put;
  const L = (pal, l, x, y) => put(x, y, ramp(pal, l, x, y));
  // roof: the parapet, the stair-head room with the black tank on top, the aerial, a bamboo pole and the washing line
  for (let x = 0; x <= HOUSE_X1; x++) for (let y = 104; y <= 107; y++) L(ROSE, y === 104 ? 0.85 : y === 105 ? 0.62 : 0.48, x, y);
  for (let x = 40; x <= 62; x++) for (let y = 86; y <= 103; y++) {
    let l = 0.4 + (x >= 58 ? 0.14 : 0) + (hash(x >> 1, y >> 2, 36) - 0.5) * 0.1;
    if (x === 62) l = 0.24;
    L(ROSE, l, x, y);
  }
  for (let x = 39; x <= 63; x++) { L(ROSE, 0.86, x, 84); L(ROSE, 0.5, x, 85); }
  for (let y = 91; y <= 103; y++) for (let x = 44; x <= 50; x++) put(x, y, x === 44 || y === 91 ? '#1e1418' : x === 49 ? '#c89058' : x === 50 ? '#3a2a24' : '#2c2024');
  for (let y = 71; y <= 83; y++) for (let x = 44; x <= 57; x++) {
    const nx = (x - 50.5) / 7;
    let col = nx > 0.45 ? '#2c2e3c' : nx < -0.6 ? '#0e0f14' : '#16171e';
    if (y === 74 || y === 78) col = '#24252e';
    if (y === 83) col = '#101016';
    put(x, y, col);
  }
  for (let y = FLUE.y + 1; y <= 83; y++) { put(FLUE.x, y, '#2a2832'); put(FLUE.x + 1, y, '#16151c'); }   // the kitchen flue
  put(FLUE.x - 1, FLUE.y + 1, '#2a2832'); put(FLUE.x + 2, FLUE.y + 1, '#16151c');
  for (let x = 47; x <= 54; x++) { put(x, 70, x > 51 ? '#3a3e52' : '#1c1d26'); if (x > 47 && x < 54) put(x, 69, '#22232c'); }
  for (let y = 56; y <= 83; y++) put(60, y, '#2c2c38');
  for (const [y, hw] of [[58, 6], [62, 5], [66, 4]]) for (let x = 60 - hw; x <= 60 + hw; x++) put(x, y, '#363646');
  for (let y = 84; y <= 103; y++) put(3, y, y % 4 === 0 ? '#4a4030' : '#6a5a3e');
  const sag = x => Math.round(88 + 4 * Math.sin(Math.PI * (x - 3) / 37));
  for (let x = 3; x <= 40; x++) put(x, sag(x), '#3a3440');
  for (let x = 67; x <= 72; x++) for (let y = 99; y <= 103; y++) put(x, y, y === 99 ? '#8a5038' : x === 72 ? '#4a2a20' : '#6a3a28');   // a tulsi pot
  for (const [x, y] of [[69, 98], [70, 97], [68, 97], [70, 96], [69, 95], [71, 95], [69, 94]]) put(x, y, '#2a4a30');
  // upper floor: rose lime wash, weathered, with rain streaks under the parapet
  for (let x = 0; x <= HOUSE_X1; x++) for (let y = 108; y <= 149; y++) {
    let l = 0.42 + (hash(x >> 2, y >> 2, 31) - 0.5) * 0.1;
    if (hash(x, 32) < 0.18 && y < 108 + 30 * hash(x, 33)) l -= 0.1;
    if (x === HOUSE_X1) l = 0.22;
    if (y === 108) l = 0.24;
    L(ROSE, l, x, y);
  }
  // its barred window, warm inside, a printed curtain half drawn
  for (let x = 3; x <= 21; x++) { L(ROSE, 0.78, x, 111); L(ROSE, 0.3, x, 112); }
  for (let y = 114; y <= 133; y++) for (let x = 5; x <= 19; x++) {
    let col = mixHex('#ffc878', '#d8843e', (y - 114) / 20);
    if (x === 5 || x === 19 || y === 114 || y === 133) col = '#2a1a1e';
    else if (x <= 10) col = ((x + y) % 3 === 0) ? '#b85848' : '#8a3438';
    else if ((x - 5) % 3 === 0 || y === 123) col = '#3a2420';
    put(x, y, col);
  }
  // the sunshade
  for (let x = 0; x <= HOUSE_X1 + 2; x++) { put(x, 150, ramp(ROSE, 0.8, x, 150)); put(x, 151, ramp(ROSE, 0.5, x, 151)); put(x, 152, '#140e18'); }
  // ground floor: blue lime wash, the barred window, a tube light, the lit door with its curtain, a diya's niche
  for (let x = 0; x <= HOUSE_X1; x++) for (let y = 153; y <= HOUSE_BASE; y++) {
    const door = Math.max(0, 1 - Math.hypot((x - 53) / 26, (y - 186) / 30));
    let l = 0.34 + door * 0.5 + (hash(x >> 2, y >> 2, 35) - 0.5) * 0.1;
    if (y === 153) l = 0.12;
    if (x === HOUSE_X1) l = 0.16;
    if (y >= 194) l -= 0.12;                                       // the damp plinth band
    L(BLUE, l, x, y);
  }
  for (let y = 165; y <= 184; y++) for (let x = 8; x <= 27; x++) {
    let col = mixHex('#ffd088', '#e09048', (y - 165) / 20);
    if (x === 8 || x === 27 || y === 165 || y === 184) col = '#1a1418';
    else if ((x - 8) % 4 === 0) col = '#2a1c1a';
    put(x, y, col);
  }
  for (let x = 6; x <= 29; x++) put(x, 185, '#5a6880');
  for (let x = 31; x <= 40; x++) { put(x, 160, '#eaf4ff'); put(x, 161, '#2a3040'); }
  for (let y = 168; y <= HOUSE_BASE; y++) for (let x = 45; x <= 61; x++) {
    let col = mixHex('#ffd898', '#d88a4a', (y - 168) / 32);
    if (x <= 46 || x >= 60 || y <= 169) col = '#2a1a16';
    else if (x <= 48) col = '#3a2620';                              // the open leaf
    else if (y <= 186 && x >= 52 + Math.round((y - 170) * 0.3)) col = ((x + y * 2) % 5 === 0) ? '#c8a040' : '#2c5a4a';   // curtain
    put(x, y, col);
  }
  for (let x = 43; x <= 63; x++) { put(x, HOUSE_BASE - 1, '#6a6070'); put(x, HOUSE_BASE, '#4a4250'); }
  for (let y = 177; y <= 182; y++) for (let x = 66; x <= 70; x++) put(x, y, y === 177 || x === 66 || x === 70 ? '#141a28' : '#3a2a20');
  return p;
}
// the terrace, the ghat's brick face into the river, the steps, the charpoy and the kheer parapet
function renderGhat(p) {
  const put = p.put, L = (pal, l, x, y) => put(x, y, ramp(pal, l, x, y));
  for (let x = 0; x <= 120; x++) for (let y = HOUSE_BASE + 1; y <= TERR; y++) {
    const spill = Math.max(0, 1 - Math.hypot((x - 53) / 30, (y - 200) / 10));
    let l = 0.42 + spill * 0.4 + (((x + (y & 1) * 2) & 3) === 0 ? -0.1 : 0);
    if (y === TERR) l = 0.66;
    if (x > 112) continue;
    L(BRICK, l, x, y);
  }
  // the ghat's brick face down to the river (lakhori courses, damp and mossy at the waterline)
  for (let x = 0; x <= 112; x++) for (let y = TERR + 1; y < NEAR_WL; y++) {
    const k = y - TERR - 1, course = k % 3 === 2, joint = ((x + (k / 3 | 0) * 4) % 9) === 0;
    let l = 0.4 - k * 0.006 + (course || joint ? -0.14 : 0) + (hash(x >> 2, y >> 1, 41) - 0.5) * 0.1;
    if (k === 0) l = 0.22;
    if (y >= NEAR_WL - 4) l -= 0.14 + (hash(x, 42) < 0.4 ? 0.06 : 0);
    if (x === 112) l -= 0.1;
    L(BRICK, l, x, y);
    if (y >= NEAR_WL - 2 && hash(x >> 1, y, 43) < 0.18 + 0.2 * Math.sin(x * 0.13)) put(x, y, '#1c2620');   // moss
  }
  // the steps down into the river at the ghat's end, lowest (widest) first
  for (let k = 4; k >= 0; k--) {
    const y0 = TERR + 1 + k * 4, x1 = 124 + k * 6;
    for (let x = 113; x <= x1; x++) for (let y = y0; y < NEAR_WL; y++) {
      let l = y === y0 ? 0.74 - k * 0.06 : y === y0 + 1 ? 0.24 : 0.36 - (y - y0) * 0.01;
      if (x === x1 && y > y0) l = 0.2;
      if (y >= NEAR_WL - 3) l -= 0.12;
      L(BRICK, l, x, y);
    }
  }
  // the charpoy: four legs, a wooden frame and the rope weave seen from above
  for (const [x, y0] of [[5, 199], [32, 199], [9, 197], [28, 197]]) for (let y = y0; y <= y0 + 5; y++) put(x, y, '#3a2418');
  for (let x = 5; x <= 32; x++) { put(x, 197, '#8a5a34'); put(x, 198, '#5a3820'); }
  for (let x = 7; x <= 30; x++) for (let y = 194; y <= 196; y++) put(x, y, ((x + y) % 2) ? '#9a8664' : '#6e5c44');
  for (let x = 7; x <= 30; x++) put(x, 193, '#7a5032');
  // the parapet at the terrace's river end, the kheer bowls cooling under muslin, two diyas
  for (let x = 80; x <= 112; x++) for (let y = 197; y <= TERR; y++) L(BRICK, y === 197 ? 0.86 : y === 198 ? 0.6 : 0.4 + (((x + (y & 1) * 2) & 3) === 0 ? -0.08 : 0), x, y);
  for (const bx of [82, 93]) {
    for (let x = bx; x <= bx + 4; x++) { put(x, 196, '#7a4430'); put(x, 195, x === bx || x === bx + 4 ? '#7a4430' : '#bdb8cc'); }
    for (let x = bx + 1; x <= bx + 3; x++) put(x, 194, '#d0ccdc');
  }
  for (const dx of [89, 100]) { put(dx - 1, 196, '#6a3424'); put(dx, 196, '#7a4430'); put(dx + 1, 196, '#6a3424'); }
}

// a string of small bulbs in three swags under the sunshade (the festive season: Sharad Purnima, Diwali a fortnight off)
const BULBS = [];
for (let x = 1; x <= HOUSE_X1; x += 3) { const s = (x % 26) / 26; BULBS.push({ x, y: 154 + Math.round(Math.sin(Math.PI * s) * 3), c: ['#ffd890', '#ffb060', '#ff7a5a', '#ffe8c0', '#b8e070'][(x / 3 | 0) % 5] }); }
function drawBulbs(t) {
  ctx.fillStyle = '#2a2026';
  for (let x = 0; x <= HOUSE_X1; x++) { const s = (x % 26) / 26; ctx.fillRect(x, 153 + Math.round(Math.sin(Math.PI * s) * 3), 1, 1); }
  BULBS.forEach((b, i) => { const on = 0.5 + 0.5 * Math.max(0, wave(t, 80, i * 0.11)); ctx.globalAlpha = on; ctx.fillStyle = b.c; ctx.fillRect(b.x, b.y, 1, 1); ctx.globalAlpha = 1; if (i % 2 === 0) halo(HL.bulb, b.x, b.y, on * 0.8); });
}

/* ================= The painted wall ad (the billboard) ================= */
// Painted straight onto the house's upper floor facing the river, as North Indian walls are: AGRA KA PETHA (Agra's
// own sweet, candied ash gourd) in red on mustard, a steel plate of the pale cubes beside it, under a bracket bulb.
const BB_LAMP = { x: BB.x + 22, y: BB.y - 5 };           // the bulb under its tin shade
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let col = ramp(['#f0cc58', '#e4b844', '#d6a434', '#c89028'], y / (h - 1) * 0.9 + Math.abs(x - 22) / 60, x, y);
    if (x === 0 || y === 0 || x === w - 1 || y === h - 1) col = '#a82a24';
    put(x, y, col);
  }
  for (let x = 2; x <= 11; x++) { put(x, 14, '#c8ccd6'); put(x, 15, '#7a7e8a'); }
  put(1, 14, '#9a9eaa'); put(12, 14, '#9a9eaa');
  const cube = (x0, y0) => {
    for (let y = y0; y < y0 + 4; y++) for (let x = x0; x < x0 + 4; x++) put(x, y, y === y0 ? '#ffffff' : x === x0 + 3 ? '#b4c094' : '#e6eecc');
    put(x0 + 1, y0 + 1, '#ffffff');
  };
  cube(2, 10); cube(7, 10); cube(4, 6);
  const c = p.done(), x = p.x;
  signText(x, 'AGRA KA', 13, 2, '#1e5a32');
  [...'PETHA'].forEach((ch, i) => { glyph(x, BOLD[ch], 16 + i * 5, 10, '#6a1418'); glyph(x, BOLD[ch], 15 + i * 5, 9, '#c4262a'); });
  return gradeAd(c, { level: 0.72, tint: '#1a2348', mix: 0.14, wear: 0.42, seed: 17 });
}
function renderBillboardFrame() {
  const p = painter(W, H), put = p.put;
  for (let x = BB.x - 2; x <= BB.x + BB.w + 1; x++) for (let y = BB.y - 2; y <= BB.y + BB.h + 1; y++) {
    if (x >= BB.x && x < BB.x + BB.w && y >= BB.y && y < BB.y + BB.h) continue;
    put(x, y, (x + y) & 1 ? '#22305a' : '#1c2850');                      // a painted blue border
  }
  for (let y = BB_LAMP.y - 6; y <= BB_LAMP.y - 2; y++) put(BB_LAMP.x, y, '#2a2430');   // the bracket
  for (let x = BB_LAMP.x; x <= BB_LAMP.x + 9; x++) put(x, BB_LAMP.y - 6, '#2a2430');
  for (let x = BB_LAMP.x - 2; x <= BB_LAMP.x + 2; x++) put(x, BB_LAMP.y - 1, x === BB_LAMP.x + 2 ? '#3a3a48' : '#5a5a6a');
  for (let x = BB_LAMP.x - 1; x <= BB_LAMP.x + 1; x++) put(x, BB_LAMP.y - 2, '#4a4a5a');
  return p.done();
}
function drawBillboard(t, pitch) {
  ctx.drawImage(BB_FRAME, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  ctx.fillStyle = '#ffe2a8'; ctx.fillRect(BB_LAMP.x - 1, BB_LAMP.y, 3, 1);
  halo(HL.bbLamp, BB_LAMP.x, BB_LAMP.y + 1, 0.95);
  halo(HL.bbWash, BB_LAMP.x, BB.y + 6, 0.8);
}

/* ================= The jetty, the moored boat and the harsingar tree (right) ================= */
function renderNearRight() {
  const p = painter(W, H), put = p.put, ca = painter(W, 120), cb = painter(W, 120), bp = painter(W, H);
  // the harsingar first: a stout trunk on the bank behind the jetty forking into limbs, a bushy crown of small leaves
  // over the top right corner in two layers that sway apart, moonlit on its upper left, its white night flowers
  // (orange tubes) all through it
  const limb = (x0, y0, x1, y1, w0, w1) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let i = 0; i <= n; i++) {
      const u = i / n, x = lerp(x0, x1, u), y = lerp(y0, y1, u), w = Math.round(lerp(w0, w1, u));
      for (let d = 0; d < w; d++) put(Math.round(x - w / 2) + d, y, d === 0 ? '#3c3446' : d === w - 1 ? '#0e0a10' : (hash(i, d, 57) < 0.15 ? '#241c26' : '#18121c'));
    }
  };
  limb(468, JETTY.y + 2, 463, 122, 9, 6); limb(463, 124, 432, 52, 5, 2); limb(464, 122, 472, 40, 5, 2);
  limb(462, 104, 444, 80, 3, 2); limb(466, 92, 480, 70, 3, 2); limb(450, 86, 424, 30, 3, 1);
  const r = rng(91), clumps = [];
  for (let i = 0; i < 400 && clumps.length < 70; i++) {
    const x = 404 + r() * 82, y = -8 + r() * 112;
    if (y > 24 + (x - 404) * 1.05 || (x < 414 && y > 36)) continue;
    clumps.push({ x, y, r: 6 + r() * 8, layer: clumps.length % 2 });
  }
  for (let y = 0; y < 116; y++) for (let x = 398; x < W; x++) {
    let best = null, bd = 9;
    for (const c of clumps) { const d = Math.hypot(x - c.x, (y - c.y) * 1.2) / c.r; if (d < bd) { bd = d; best = c; } }
    if (!best || bd >= 1) continue;
    const h1 = hash(x, y, 53), q = best.layer ? cb : ca;
    if (bd > 0.78 && h1 < (bd - 0.78) * 4) continue;                               // ragged leafy edge
    if (bd < 0.55 && hash(x >> 1, y >> 1, 52) < 0.04) continue;                     // a gap to the limbs behind
    const lit = (x - best.x) * 0.8 + (y - best.y) < -best.r * 0.3, under = (y - best.y) > best.r * 0.45;
    let col = h1 < 0.3 ? '#0b1513' : h1 < 0.72 ? '#12201d' : '#192a26';
    if (lit) col = h1 < 0.25 ? '#1c302b' : h1 < 0.8 ? '#28433c' : '#35564c';
    if (under) col = h1 < 0.5 ? '#08100f' : '#0e1816';
    q.put(x, y, col);
    if (h1 > 0.978 && !under && hash(x >> 2, y >> 2, 54) < 0.6) { q.put(x, y, lit ? '#f2eef6' : '#cfcadc'); q.put(x, y + 1, '#c8682a'); }   // a flower
  }
  // the jetty: a plank deck on posts out to the right edge, and the lantern's pole
  for (let x = JETTY.x0; x < W; x++) {
    put(x, JETTY.y, (x - JETTY.x0) % 7 === 0 ? '#4a3424' : hash(x, 61) < 0.2 ? '#9a7a58' : '#86684a');
    put(x, JETTY.y + 1, '#5a4030'); put(x, JETTY.y + 2, '#2a1c18');
  }
  for (const x of [369, 392, 415, 438, 461]) for (let y = JETTY.y + 3; y < H; y++) { put(x, y, '#3a2c26'); put(x + 1, y, '#1e1614'); }
  for (let y = LANTERN.y - 6; y < JETTY.y; y++) put(368, y, '#3a2e28');
  for (let x = 368; x <= 372; x++) put(x, LANTERN.y - 7, '#3a2e28');
  for (let y = LANTERN.y - 3; y <= LANTERN.y + 3; y++) for (let x = LANTERN.x - 2; x <= LANTERN.x + 2; x++) {
    const edge = x === LANTERN.x - 2 || x === LANTERN.x + 2 || y === LANTERN.y - 3 || y === LANTERN.y + 3;
    put(x, y, edge ? '#2a2420' : mixHex('#ffe0a0', '#e09048', (y - LANTERN.y + 2) / 5));
  }
  put(LANTERN.x, LANTERN.y - 4, '#2a2420'); put(LANTERN.x, LANTERN.y - 5, '#2a2420');
  // the moored boat in front of the jetty (its own layer: it bobs): a long Yamuna rowing boat, its bow raised to the left
  const B0 = 312, B1 = 412, GW = MOORED_GW;
  for (let x = B0; x <= B1; x++) {
    const u = (x - B0) / (B1 - B0), rise = Math.round(Math.pow(Math.max(0, 0.12 - u) / 0.12, 1.6) * 7 + Math.pow(Math.max(0, u - 0.94) / 0.06, 2) * 2);
    const top = GW - rise, depth = Math.round(7 - 3 * Math.abs(u - 0.5) * 2) + (u < 0.1 ? 2 : 0);
    bp.put(x, top - 1, '#24181a');                                                // the far gunwale, the inside below it
    bp.put(x, top, x % 9 === 0 ? '#a07a58' : '#8a6a4e');
    for (let y = top + 1; y <= GW + depth; y++) bp.put(x, y, y === top + 3 ? '#2c4866' : y === top + 4 ? '#24405c' : (y - top) % 3 === 0 ? '#2a1c1c' : '#3a2826');
  }
  for (const x of [338, 362, 388]) { bp.put(x, GW - 2, '#6a5040'); bp.put(x, GW - 1, '#5a4434'); }   // thwarts
  for (let i = 0; i < 40; i++) bp.put(330 + i, GW - 3 - Math.round(i * 0.12), '#7a6048');           // an oar resting across
  return { right: p.done(), crownA: ca.done(), crownB: cb.done(), boat: bp.done() };
}

/* ================= The rowing boat that crosses mid-river (with the current, right to left) ================= */
// A boatman standing at the stern to his oar, a passenger on the thwart, a lantern on the bow; a stroke every 2.5 s
// leaves a ring at the blade, and a faint wake spreads behind. It takes 60 s to cross; at crowd 0 it stays away.
const CROSS = { t0: 96, t1: 156, x0: 476, x1: -44, y: BOAT.y };
const crossX = t => Math.round(lerp(CROSS.x0, CROSS.x1, (t - CROSS.t0) / (CROSS.t1 - CROSS.t0)));
function drawRowboat(t, crowd) {
  if (crowd <= 0 || t < CROSS.t0 || t >= CROSS.t1) return;
  const x = crossX(t), y = CROSS.y, stroke = frac(t / 2.5);
  ctx.fillStyle = '#4a5a88';
  for (let k = 1; k < 16; k++) { ctx.globalAlpha = 0.5 * (1 - k / 16); ctx.fillRect(x + 23 + k * 2, y + 1 + (k >> 2), 2, 1); ctx.fillRect(x + 23 + k * 2, y - (k >> 3), 1, 1); }
  for (let s = 0; s < 3; s++) {                                       // rings left by the last strokes, drifting astern
    const age = stroke + s, rx = 1 + age * 2.2;
    ctx.globalAlpha = 0.45 * (1 - age / 3);
    ctx.fillRect(Math.round(x + 25 + age * 5 - rx), y + 3, Math.round(rx * 2), 1);
  }
  ctx.globalAlpha = 1;
  streak(ctx, t, x + 2, 2, '#ffcf88', 0.4, 13, y + 3, y + 30);
  ctx.fillStyle = '#5a4838'; ctx.fillRect(x, y - 1, 2, 1);
  ctx.fillStyle = '#6a5444'; ctx.fillRect(x + 1, y, 23, 1);
  ctx.fillStyle = '#241a1c'; ctx.fillRect(x + 2, y + 1, 21, 1); ctx.fillRect(x + 4, y + 2, 17, 1);
  const tip = stroke < 0.55 ? [x + 26, y + 3] : [x + 21, y + 2];      // the blade in the water, then swung forward
  for (let i = 0; i <= 10; i++) px(ctx, Math.round(lerp(x + 17, tip[0], i / 10)), Math.round(lerp(y - 8, tip[1], i / 10)), '#3a3040');
  ctx.fillStyle = '#ffd890'; ctx.fillRect(x + 2, y - 3, 1, 2);
  halo(HL.boat, x + 2, y - 2, 0.9 + 0.1 * wave(t, 160));
  const rider = CAST.find(c => c.id === 'rider'), rower = CAST.find(c => c.id === 'rower');
  if (rider.rank < crowd) person(x + 10, y - 1, rider.P, rider.o);
  person(x + 18, y - 1, rower.P, { ...rower.o, aR: stroke < 0.55 ? 3 : 0 });
}

/* ================= People ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#e6b48c', '#c8915e', '#a8703e', '#7c4e2c'], HAIRS = ['#14100e', '#2a1a14', '#4a3020', '#c8c4c0', '#6a3a1c'];
const NIGHT = '#0e1430', WARM = '#ffcf98', LAMPC = '#ffdc9a', MOONC = '#c8ccf0';
/** a look: the proportions for a height of h px, its colours dimmed for the night, its shoulders lit by `glow` */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.28, m = c => mixHex(c, NIGHT, dk), g = o.glow || WARM;
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], g, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, g, 0.35), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, shawl: o.shawl ? m(o.shawl) : null, dup: o.dup ? m(o.dup) : null };
}
/**
 * One person, feet on row fy, centred on column x (after rome.js). o.view: 0 from behind, 1 facing us, 2 in profile
 * (o.dir 1 right, -1 left); o.sit: seated on an edge at fy; o.lean: head px towards a neighbour;
 * o.aL / o.aR: 0 down, 3 out (carrying), 4 bent up (a camera, a glass at the mouth)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side && o.step) {                                  // mid-stride: one leg forward, one back
      R(x - 2, ly, 4, 2, legC);
      R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
      R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      if (P.skirt) R(x - 2, ly, 4, legs - 2, P.skirt);
    } else if (side) { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); if (P.skirt) R(x - 2, ly, 4, legs, P.skirt); }
    else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx - 1, ly, bw + 2, legs, P.skirt);
    }
  } else if (o.sit && side) { R(dir > 0 ? x - 2 : x - 3, fy - 1, 6, 2, P.bot); if (!o.cross) R(x + 3 * dir, fy + 1, 1, 3, P.bot); }
  else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);
  if (P.shawl) { R(tx - (side ? 0 : 1), ty, bw + (side ? 0 : 2), Math.max(2, th >> 1), P.shawl); R(tx, ty, bw, 1, mixHex(P.shawl, '#ffcf98', 0.25)); }
  if (P.dup) { if (view === 0) R(tx, ty + 1, bw, 1, P.dup); else R(side ? (dir > 0 ? tx : tx + bw - 1) : tx, ty, 1, th - 1, P.dup); }
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { R(x, ty + 1, 1, th - 2, P.topD); R(x, ty + th - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 2 - k, 1, 1, k < 2 ? P.topD : P.skin); R(x + dir * 4 - (dir < 0 ? 1 : 0), ty - 3, 2, 3, '#0e1018'); R(x + dir * 4 - (dir < 0 ? 0 : -1), ty - 3, 1, 3, '#8ab8e8'); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#8ab8e8'); }
      else { R(ax, ty - 1, 1, 3, P.topD); R(ax + (sg < 0 ? 1 : -1), ty - 2, 1, 1, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx - 1, hy, hw + 2, 1, P.hat); }
  if (P.dup && view !== 1) R(hx, hy - 1, hw, 1, P.dup);              // the dupatta over her head
  return hy;
}
// The people who stay, each with a verb (ranks for the People setting: 0 is there at every level above 0). A couple
// shares a rank; the boatman and his passenger go with the crossing boat.
const CAST = [
  { id: 'nana', x: 20, y: 193, rank: 0, P: look(22, 2, 3, '#d4ccc0', '#c8c0b8', { shawl: '#6a5444' }), o: { view: 2, dir: 1, sit: true, cross: true } },
  { id: 'nani', x: 56, y: 199, rank: 0, P: look(22, 2, 3, '#7a3a5a', '#5a3048', { dup: '#d8a840', long: 2 }), o: { view: 2, dir: 1 } },
  { id: 'girl', x: 110, y: 196, rank: 0.15, P: look(15, 1, 0, '#e07a38', '#3a3a58', { long: 2 }), o: { view: 0, sit: true } },
  { id: 'photo', x: 425, y: 204, rank: 0.4, P: look(22, 0, 1, '#3a5274', '#22202c', { glow: LAMPC, hat: '#2a2a34' }), o: { view: 0, aL: 4, aR: 4 } },
  { id: 'him', x: 386, y: 204, rank: 0.45, P: look(20, 1, 0, '#4a3a58', '#22202c', { glow: LAMPC }), o: { view: 0, sit: true } },
  { id: 'her', x: 394, y: 204, rank: 0.45, P: look(19, 1, 1, '#a84a68', '#2a2a3a', { glow: LAMPC, long: 3, dup: '#d8c8a0' }), o: { view: 0, sit: true, lean: -1 } },
  { id: 'majhi', x: 401, y: 213, rank: 0.3, P: look(19, 3, 0, '#c8c0b0', '#6a5444', { glow: LAMPC, shawl: '#5a4a3c' }), o: { view: 2, dir: -1, sit: true, cross: true } },
  { id: 'fisher', x: 128, y: 214, rank: 0.5, P: look(20, 3, 1, '#5a6a5a', '#3a3440', { glow: WARM }), o: { view: 2, dir: 1, sit: true } },
  { id: 'teen', x: 74, y: 103, rank: 0.75, P: look(20, 1, 0, '#c84a4a', '#2a2a3a', { glow: MOONC }), o: { view: 0, sit: true, aR: 2 } },
  { id: 'boy', x: 446, y: 204, rank: 0.85, P: look(14, 2, 0, '#e8c040', '#3a3a58', { glow: LAMPC }), o: { view: 0, sit: true } },
  { id: 'rower', x: 0, y: 0, rank: 0, P: look(12, 3, 0, '#c8c0b0', '#4a4040', { glow: MOONC, dk: 0.45 }), o: { view: 2, dir: -1 } },
  { id: 'rider', x: 0, y: 0, rank: 0.5, P: look(10, 1, 1, '#8a4a6a', '#2a2a3a', { glow: MOONC, dk: 0.45, dup: '#c8b890' }), o: { view: 2, dir: -1, sit: true } },
];
// the kheer routine, every 40 s: out of the door, across the terrace to the parapet, a bowl set down (even turns) or
// taken back in (odd turns, so the loop ends as it began), back to the door and inside
const KHEER = { per: 40, door: 56, para: 78, bowl: 103 };
function nani(t) {
  const c = Math.floor(t / KHEER.per), u = t % KHEER.per, out = (c & 1) === 0;
  const holding = out ? u < 16 : u >= 16 && u < 32;
  let x = KHEER.door, y = 199, dir = 1, view = 2, walk = false, a = 1;
  if (u < 3) { view = 1; a = smooth(0, 2, u); }
  else if (u < 12) { const k = (u - 3) / 9; x = lerp(KHEER.door, KHEER.para, k); y = Math.round(lerp(199, 204, smooth(0, 0.3, k))); walk = true; }
  else if (u < 20) { x = KHEER.para; y = 204; }
  else if (u < 29) { const k = (u - 20) / 9; x = lerp(KHEER.para, KHEER.door, k); y = Math.round(lerp(204, 199, smooth(0.7, 1, k))); dir = -1; walk = true; }
  else if (u < 32) { view = 0; a = 1 - smooth(30, 32, u); }
  else a = 0;
  const bowlOnParapet = out ? u >= 16 : u < 16;
  return { x: Math.round(x), y, dir, view, walk, a, holding, bowlOnParapet };
}
const CHAMKI = [[54, 76], [174, 196]];                 // the two chamki moments (see the timeline)
function drawPeople(t, crowd, bob) {
  const k = nani(t), list = [];
  for (const c of CAST) {
    if (c.rank >= crowd || c.id === 'rower' || c.id === 'rider') continue;
    if (c.id === 'nani' && k.a <= 0.02) continue;
    list.push({ c, y: c.id === 'nani' ? k.y : c.y + (c.id === 'majhi' ? bob : 0) });
  }
  list.sort((a, b) => a.y - b.y);
  const point = inEvent(t, [[58, 67], [178, 187]]);
  for (const { c, y } of list) {
    const o = { ...c.o }, P = c.P;
    let x = c.x;
    const ty = y - (o.sit ? 0 : P.ll) - P.th + 1;
    if (c.id === 'nana') { o.aR = (t + 3) % 15 < 4 ? 4 : 3; }
    else if (c.id === 'nani') { x = k.x; o.dir = k.dir; o.view = k.view; o.step = k.walk && (step(t, 768) & 1); o.aR = k.holding ? 3 : 0; ctx.globalAlpha = k.a; }
    else if (c.id === 'girl') { o.lean = Math.round(wave(t, 8, 0.2)); if (point) o.aR = 1; }
    else if (c.id === 'photo') {
      const pause = t % 48 >= 30 && t % 48 < 37;
      R(413, 184, 6, 3, '#16161e'); R(414, 183, 3, 1, '#22222c'); R(419, 185, 2, 1, '#2a2a34');
      px(ctx, 413, 185, inEvent(t, CHAMKI) && frac(t) < 0.5 ? '#ff4a3a' : '#6a1a18');
      for (let j = 0; j < 17; j++) { px(ctx, 415 - Math.round(j * 0.3), 187 + j, '#2a2a34'); px(ctx, 416, 187 + j, '#2a2a34'); px(ctx, 417 + Math.round(j * 0.3), 187 + j, '#24242e'); }
      if (pause) { x += 3; o.aL = 0; o.aR = 2; o.view = 0; }
    }
    else if (c.id === 'her') o.lean = t % 30 >= 20 && t % 30 < 26 ? 0 : -1;
    else if (c.id === 'him') { if (point) o.aR = 1; }
    else if (c.id === 'boy') o.aR = t % 20 < 2 ? 1 : 0;
    person(x, y, P, o);
    ctx.globalAlpha = 1;
    if (c.id === 'nana') { if (o.aR === 4) { px(ctx, x + 3, ty - 1, '#d8dce8'); px(ctx, x + 3, ty, '#9aa0b0'); } else { px(ctx, x + 4, ty + 2, '#d8dce8'); px(ctx, x + 4, ty + 3, '#9aa0b0'); } }
    if (c.id === 'nani' && k.holding) { ctx.globalAlpha = k.a; R(x + 4 * k.dir - (k.dir < 0 ? 4 : 0), ty + 1, 5, 1, '#7a4430'); R(x + 4 * k.dir - (k.dir < 0 ? 3 : -1), ty, 3, 1, '#cfcadb'); ctx.globalAlpha = 1; }
    if (c.id === 'majhi') {                                          // his bidi glows on a draw every 12 s, smoke rises
      const u = t % 12;
      px(ctx, x - 4, ty - 2, u < 1.5 ? '#ffc070' : '#c8582a');
      for (let s = 0; s < 3; s++) { const v = (u + s * 1.2) / 6; if (v < 1) { ctx.globalAlpha = 0.35 * (1 - v); px(ctx, x - 4 + Math.round(wave(t, 40, s * 0.3) - v * 2), ty - 3 - Math.round(v * 9), '#9a9aa8'); } }
      ctx.globalAlpha = 1;
    }
    if (c.id === 'fisher') {                                         // his rod out over the water, the line twitching now and then
      const tipX = x + 30, tipY = ty - 12, tw = t % 24 < 1 ? 1 : 0;
      for (let j = 0; j <= 28; j++) px(ctx, x + 2 + j, Math.round(ty + 2 - j * (14 / 28) + (j * j) / 260), '#4a3a2a');
      ctx.fillStyle = '#6a7090'; ctx.globalAlpha = 0.6; ctx.fillRect(tipX, tipY + 1, 1, NEAR_WL - tipY + tw); ctx.globalAlpha = 1;
    }
    if (c.id === 'boy' && t % 20 < 4) {                              // a pebble's ring on the water below
      const v = (t % 20) / 4; ctx.globalAlpha = 0.5 * (1 - v); ctx.fillStyle = '#7a88b8';
      ctx.fillRect(Math.round(x + 6 - v * 4), 226, Math.round(2 + v * 8), 1); ctx.globalAlpha = 1;
    }
  }
  // the bowl the routine leaves on the parapet
  if (crowd > 0 && k.bowlOnParapet) { R(KHEER.bowl, 196, 5, 1, '#7a4430'); R(KHEER.bowl + 1, 195, 3, 1, '#bdb8cc'); R(KHEER.bowl + 1, 194, 3, 1, '#d0ccdc'); }
}

/* ================= Falling harsingar blossoms (the city's own weather) ================= */
const FLOWERS = [
  { count: 22, nMin: 3, nMax: 5, x0: 0, x1: W, land: SHORE + 4, a: 0.5 },
  { count: 12, nMin: 4, nMax: 7, x0: 300, x1: W + 20, land: 232, a: 0.8 },
  { count: 6, nMin: 5, nMax: 8, x0: 360, x1: W + 30, land: H + 6, a: 1 },
];
function drawFlowers(depth, t) {
  const L = FLOWERS[depth];
  for (let i = 0; i < L.count; i++) {
    const n = L.nMin + Math.floor(hash(i, depth, 81) * (L.nMax - L.nMin + 1)), { cyc, u } = cycle(t, n, hash(i, depth, 82));
    const h1 = hash(i, cyc, 83 + depth), h2 = hash(i, cyc, 86 + depth);
    const x = Math.round(lerp(L.x0, L.x1, h1) - u * (30 + depth * 30) + 4 * Math.sin(TAU * (u * 3 + h2))), y = Math.round(lerp(depth === 2 ? 20 : -6, L.land, u));
    ctx.globalAlpha = L.a * Math.min(1, (1 - u) * 6);
    if (depth === 0) px(ctx, x, y, '#b8b4cc');
    else if (depth === 1) { px(ctx, x, y, '#dcd8e8'); px(ctx, x + 1, y, '#c8c4d8'); px(ctx, x, y + 1, '#c8682a'); }
    else {
      const spin = (step(t, 1920) + i) & 1;
      if (spin) { px(ctx, x - 1, y, '#e8e4f0'); px(ctx, x + 1, y, '#e8e4f0'); px(ctx, x, y - 1, '#f4f0fa'); px(ctx, x, y + 1, '#d8d4e4'); }
      else { px(ctx, x - 1, y - 1, '#e8e4f0'); px(ctx, x + 1, y - 1, '#f4f0fa'); px(ctx, x - 1, y + 1, '#d8d4e4'); px(ctx, x + 1, y + 1, '#e8e4f0'); }
      px(ctx, x, y, '#e0782e');
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= The near ledge: Mehtab Bagh's sandstone river wall, where the viewer leans ================= */
// diyas set out along it for the full moon, marigold heads, a brass lota and a clay cup of chai
const LEDGE = 254;                    // the ledge's top row
const DIYAS = [22, 63, 118, 171, 236, 301, 356, 409, 452];
function renderLedge() {
  const p = painter(W, H), put = p.put;
  for (let x = 0; x < W; x++) for (let y = LEDGE; y < H; y++) {
    const warm = Math.max(0, ...DIYAS.map(d => 1 - Math.hypot((x - d) / 22, (y - LEDGE) / 7)));
    const top = y < LEDGE + 4, joint = !top && ((x + (y > 262 ? 11 : 0)) % 23 === 0 || y === 262);
    let l = top ? 0.62 - (y - LEDGE) * 0.08 : 0.32 - (y - LEDGE) * 0.012;
    if (y === LEDGE) l = 0.82;
    if (joint) l -= 0.12;
    l += (hash(x >> 1, y, 71) - 0.5) * 0.06;
    let col = ramp(SS, l, x, y);
    if (warm > 0) col = mixHex(col, '#ffb060', warm * (top ? 0.42 : 0.2));
    put(x, y, col);
  }
  for (const d of DIYAS) { put(d - 1, LEDGE - 1, '#7a3a24'); put(d, LEDGE - 1, '#9a5030'); put(d + 1, LEDGE - 1, '#7a3a24'); put(d - 2, LEDGE, '#5a2a1c'); put(d + 2, LEDGE, '#5a2a1c'); }
  for (const [x, n] of [[30, 3], [128, 2], [246, 3], [366, 2], [440, 3]]) for (let k = 0; k < n; k++) { put(x + k * 2, LEDGE - 1, '#e8862a'); put(x + k * 2 + 1, LEDGE - 1, '#c86a1c'); put(x + k * 2, LEDGE - 2, '#f0a040'); }
  for (let y = LEDGE - 6; y < LEDGE; y++) for (let x = 84; x <= 89; x++) {                         // a brass lota
    const w = y <= LEDGE - 5 ? 1 : 3, c = 86.5;
    if (Math.abs(x - c) <= w) put(x, y, x > c ? '#e0b860' : x < c - 1 ? '#7a5a2a' : '#b08840');
  }
  for (let y = LEDGE - 4; y < LEDGE; y++) for (let x = 390; x <= 393; x++) put(x, y, y === LEDGE - 4 ? '#3a2018' : x === 393 ? '#6a3424' : '#8a4a30');   // a kulhad
  return p.done();
}
const FLAME = ['#ffe6a0', '#fff0c0', '#ffd27a'];
function drawDiyas(t) {
  const s = step(t, 1440);
  DIYAS.forEach((d, i) => {
    const h1 = hash(i, s, 73), f = 0.82 + 0.18 * h1;
    ctx.fillStyle = FLAME[Math.floor(h1 * 3)]; ctx.fillRect(d, LEDGE - 2, 1, 1);
    if (h1 > 0.25) { ctx.fillStyle = '#ff9a3a'; ctx.fillRect(d + (h1 > 0.85 ? 1 : 0), LEDGE - 3, 1, 1); }
    const pool = 0.6 + 0.2 * wave(t, 140 + i * 6, i * 0.3) + 0.2 * wave(t, 330 + i * 4, i * 0.7);
    halo(HL.diya, d, LEDGE - 3, f); halo(HL.diyaGlow, d, LEDGE - 2, f * 0.8); halo(HL.diyaPool, d, LEDGE + 1, pool);
  });
}
// diyas afloat on leaf cups, set out from the jetty and carried left by the current, one crossing a loop each
const FLOATS = [{ ph: 0.08, y: 241 }, { ph: 0.33, y: 247 }, { ph: 0.58, y: 238 }, { ph: 0.83, y: 244 }];
function drawFloats(t) {
  FLOATS.forEach((f, i) => {
    const x = Math.round(150 + frac(f.ph - t / LOOP) * 226), y = f.y + Math.round(0.6 * wave(t, 40, i * 0.17));
    const a = Math.min(smooth(150, 166, x), 1 - smooth(356, 376, x));
    if (a <= 0.02) return;
    ctx.globalAlpha = a;
    streak(ctx, t, x, 2, '#ffb860', 0.35 * a, 23 + i, y + 2, y + 14);
    ctx.globalAlpha = a;
    ctx.fillStyle = '#2a3a22'; ctx.fillRect(x - 1, y + 1, 3, 1);
    ctx.fillStyle = '#7a4a2a'; ctx.fillRect(x - 1, y, 3, 1);
    ctx.fillStyle = FLAME[(step(t, 960) + i) % 3]; ctx.fillRect(x, y - 1, 1, 1);
    ctx.globalAlpha = 1;
    halo(HL.diya, x, y - 1, a * (0.85 + 0.15 * wave(t, 300, i * 0.4)));
  });
}

/* ================= The loop's timeline =================
   t (s)          what
   0..240         water, mist, wisps, the harsingar swaying, diyas and bulbs, glints on the marble, moths, fireflies,
                  floating diyas, the bats circling the mosque's and the jawab's domes, the laundry, the TV's flicker
   every 40       the kheer routine (a bowl out on even turns, back in on odd ones)
   every 12/15    the boatman's bidi, the grandfather's chai
   18..30         fruit bats cross the moon (again 136..148)
   34.2           a fish jumps mid-river (again 112.4, 184.6, 226.2)
   40..62         a wisp drifts over the moon: the far bank dims
   54..76         chamki: the moon clears, the Taj brightens, a wave of glints climbs it; the girl and the young man point,
                  the camera records (a chime at 57)
   80..92         egrets fly home low over the water (again 206..218)
   96..156        the rowing boat crosses with the current, right to left
   160..182       the wisp again; 174..196 chamki again (a chime at 177)                                              */
const MOON_WISP = [[40, 62], [160, 182]];
const BATS_T = [[18, 30], [136, 148]];
const EGRETS_T = [[80, 92], [206, 218]];
const SPLASH = [{ t: 34.2, x: 204, y: 206 }, { t: 112.4, x: 330, y: 214 }, { t: 184.6, x: 168, y: 220 }, { t: 226.2, x: 292, y: 202 }];
const veil = t => Math.max(...MOON_WISP.map(([a]) => env(t, a + 5, a + 17, 3)));
const chamki = t => Math.max(...MOON_WISP.map(([a]) => env(t, a + 14, a + 36, 4)));
// the glint band climbs the Taj from the plinth to the finial in the chamki's first 14 s, then the glints hang all over it
const glintBand = t => { for (const [a] of MOON_WISP) if (t >= a + 14 && t < a + 28) return lerp(DECK, TIP - 4, (t - a - 14) / 14); return null; };
// points on the marble that catch the moon (the inlay, the polished faces): build time
const GLINTS = [];
{
  const r = rng(61);
  for (let n = 0; n < 9000 && GLINTS.length < 440; n++) {
    const y = 50 + Math.floor(r() * 106), x = PL.x0 + Math.floor(r() * (PL.x1 - PL.x0 + 1));
    let inside = false;
    if (y >= 56 && y <= 81) inside = Math.abs(x - CX) <= BULB[y - 56] - 1;
    else if (y >= 82 && y <= 103) inside = Math.abs(x - CX) <= 20 || (y >= 87 && CHH.some(c => Math.abs(x - c) <= 5));
    else if (y >= 106 && y <= BASE) inside = x > FAC.x0 && x < FAC.x1 && !(Math.abs(x - CX) <= 11 && y >= 109);
    else if (y > BASE + 1 && y < DECK) inside = true;
    if (y >= 86 && y < BASE) inside = inside || MIN_F.some(m => Math.abs(x - m) <= 2);
    if (inside) GLINTS.push({ x, y });
  }
}
function drawGlints(t) {
  const s = step(t, 480), ch = chamki(t), band = glintBand(t);
  for (let i = 0; i < GLINTS.length; i++) {
    const g = GLINTS[i];
    let p = 0.012;
    if (ch > 0) p += 0.15 * ch * (band == null ? 0.45 : Math.max(0, 1 - Math.abs(g.y - band) / 14));
    const h1 = hash(i, s, 63);
    if (h1 >= p) continue;
    ctx.fillStyle = h1 < p * 0.3 ? '#fff4dc' : '#ffffff';
    ctx.fillRect(g.x, g.y, 1, 1);
    if (h1 < p * 0.08 && ch > 0.3) { ctx.globalAlpha = 0.6; ctx.fillRect(g.x - 1, g.y, 3, 1); ctx.fillRect(g.x, g.y - 1, 1, 3); ctx.globalAlpha = 1; }
  }
}
// fruit bats: across the moon twice a loop, and a few circling the mosque's and the jawab's domes all night
const BAT5 = [['1...1', '.111.'], ['.....', '11111'], ['.....', '.111.', '1...1']], BAT3 = [['1.1', '.1.'], ['...', '111']];
function batAt(x, y, f, big) {
  ctx.fillStyle = '#100c18';
  (big ? BAT5 : BAT3)[f].forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') ctx.fillRect(Math.round(x) + i, Math.round(y) + j, 1, 1); });
}
function drawMoonBats(t) {
  for (const [a, b] of BATS_T) if (t >= a && t < b) {
    const u = (t - a) / (b - a);
    for (let i = 0; i < 5; i++) {
      const x = lerp(MOON.x - 96, MOON.x + 84, u) - i * 10 + 5 * Math.sin(i * 1.7), y = MOON.y - 6 + i * 3 + 2 * wave(t, 48, i * 0.2) - 10 * Math.sin(u * Math.PI);
      batAt(x, y, (step(t, 720) + i) % 3, true);
    }
  }
}
function drawDomeBats(t) {
  for (let i = 0; i < 6; i++) {
    const cx = i < 3 ? MOSQ.x0 + 33 : 2 * CX - MOSQ.x0 - 33, per = [16, 20, 24][i % 3], a = TAU * (t / per + i * 0.37);
    batAt(cx + 30 * Math.cos(a) - 1, 92 + 10 * Math.sin(a) + 3 * Math.sin(2 * a + i), (step(t, 960) + i) & 1, false);
  }
}
// egrets flying home low over the river, left to right
const EGRET = [['1...1', '.111.', '..1..'], ['.....', '11111', '..1..']];
function drawEgrets(t) {
  for (const [a, b] of EGRETS_T) if (t >= a && t < b) {
    const u = (t - a) / (b - a);
    for (let i = 0; i < 3; i++) {
      const x = Math.round(lerp(-30, W + 30, u) - i * 11), y = Math.round(182 + i * 2 + 1.5 * wave(t, 24, i * 0.3));
      ctx.fillStyle = '#c8cadc';
      EGRET[(step(t, 300) + i) & 1].forEach((row, j) => { for (let k = 0; k < row.length; k++) if (row[k] === '1') ctx.fillRect(x + k, y + j, 1, 1); });
    }
  }
}
// a fish jumps: a silver arc, the splash, then rings spreading for 3 s
function drawSplashes(t) {
  for (const s of SPLASH) {
    const d = t - s.t;
    if (d < 0 || d > 3.6) continue;
    if (d < 0.6) { const v = d / 0.6; px(ctx, Math.round(s.x - 3 + v * 6), Math.round(s.y - Math.sin(v * Math.PI) * 6), '#d8dcf0'); }
    if (d >= 0.5 && d < 1) for (let k = -1; k <= 1; k++) px(ctx, s.x + 3 + k * 2, s.y - Math.round((1 - Math.abs(k)) * 2 + (1 - d) * 3), '#aab4d8');
    if (d >= 0.55) for (let r = 0; r < 2; r++) {
      const v = (d - 0.55 - r * 0.7) / 2.4;
      if (v < 0 || v > 1) continue;
      ctx.globalAlpha = 0.55 * (1 - v); ctx.fillStyle = '#8a98c8';
      const w = Math.round(3 + v * 14);
      ctx.fillRect(s.x + 3 - (w >> 1), s.y, w, 1); ctx.globalAlpha = 1;
    }
  }
}
// the house's small life: the washing stirring on the line, the TV flickering upstairs, someone at the stove behind the
// ground-floor bars, the door curtain breathing, a gecko hunting along the wall under the ad
const LAUNDRY = [[8, 13, 98, '#5e2a36', '#40202a', 0], [17, 25, 101, '#4e5a78', '#38405a', 0.3], [29, 33, 96, '#7a6430', '#584820', 0.6]];
const sagRow = x => Math.round(88 + 4 * Math.sin(Math.PI * (x - 3) / 37));
const GECKO = ['..1.1..', '1111111', '..1.1..'];
function drawHouseLife(t) {
  for (const [x0, x1, y1, col, dk, ph] of LAUNDRY) {
    const sw = wave(t, 30, ph) * 1.4;
    for (let x = x0; x <= x1; x++) for (let y = sagRow(x) + 1; y <= y1; y++) px(ctx, x + Math.round(sw * (y - sagRow(x)) / (y1 - sagRow(x) + 1)), y, x === x1 || y === y1 ? dk : col);
  }
  ctx.globalAlpha = 0.06 + 0.36 * hash(1, step(t, 720), 75); ctx.fillStyle = '#5a7cff'; ctx.fillRect(11, 115, 8, 18); ctx.globalAlpha = 1;
  const sx = Math.round(14 + 5 * wave(t, 6, 0.1));                  // at the stove
  ctx.globalAlpha = 0.75; ctx.fillStyle = '#4a2a1c'; ctx.fillRect(sx, 172, 4, 4); ctx.fillRect(sx - 2, 176, 8, 8); ctx.globalAlpha = 1;
  ctx.fillStyle = '#2a1c1a'; for (let x = 12; x <= 24; x += 4) ctx.fillRect(x, 166, 1, 18);
  const cs = Math.round(wave(t, 20, 0.15));
  for (let y = 171; y <= 186; y++) { const e = 52 + Math.round((y - 170) * 0.3); if (cs > 0) px(ctx, e, y, mixHex('#ffd898', '#d88a4a', (y - 168) / 32)); else if (cs < 0) px(ctx, e - 1, y, '#2c5a4a'); }
  const u = t % 60, dart = v => (Math.floor(v * 8) + smooth(0, 0.5, frac(v * 8))) / 8;
  const gx = u < 24 ? 22 + 48 * dart(u / 24) : u < 30 ? 70 : u < 54 ? 70 - 48 * dart((u - 30) / 24) : 22, right = u < 24 || u >= 54;
  ctx.fillStyle = '#8a7c6c';
  GECKO.forEach((row, j) => { for (let i = 0; i < 7; i++) if (row[i] === '1') ctx.fillRect(Math.round(gx) + (right ? i : 6 - i), 145 + j, 1, 1); });
  px(ctx, Math.round(gx) + (right ? 7 : -1), 146, '#6a5e52');
}
// smoke from the kitchen flue on the stair-head room (someone is cooking at the stove below), drifting left on the breeze
const FLUE = { x: 41, y: 75 };
function drawSmoke(t) {
  for (let i = 0; i < 18; i++) {
    const { u } = cycle(t, 20, i / 18), r = Math.min(8, 2 + Math.round(u * 6));
    const x = FLUE.x - u * 34 + 3 * Math.sin(u * 5), y = FLUE.y - 1 - u * 58;
    ctx.globalAlpha = 0.13 * (1 - u) * Math.min(1, u * 8);
    ctx.drawImage(PUFF[r], Math.round(x - r), Math.round(y - r));
  }
  ctx.globalAlpha = 1;
}
// moonlight thrown up by the river, playing on the ghat's brick face and its steps just above the waterline
function drawCaustics(t) {
  ctx.fillStyle = '#6676a6';
  for (let y = NEAR_WL - 9; y < NEAR_WL; y++) {
    const k = (y - NEAR_WL + 10) / 10;
    for (let x = 0; x <= 148; x++) {
      if (x > 112 && y < TERR + 1 + ((x - 113) / 6 | 0) * 4) continue;
      const v = 0.5 * Math.sin(x * 0.5 + y * 1.3 + TAU * 30 * t / LOOP) + 0.5 * Math.sin(x * 0.23 - y * 0.7 - TAU * 18 * t / LOOP);
      if (v > 0.55 - 0.25 * k) { ctx.globalAlpha = 0.14 + 0.26 * k; ctx.fillRect(x, y, 1, 1); }
    }
  }
  ctx.globalAlpha = 1;
}
// moths round the jetty lantern and the door's light; fireflies by the tree and the steps
const MOTHS = [[LANTERN.x, LANTERN.y, 6, 4, 3], [LANTERN.x, LANTERN.y + 1, 8, 5, 4], [LANTERN.x + 1, LANTERN.y - 1, 4, 3, 5], [53, 166, 6, 3, 4], [53, 167, 9, 4, 6]];
const FIREFLIES = [[440, 150], [470, 176], [416, 128], [452, 196], [130, 202], [150, 214], [98, 182], [6, 214], [348, 222]];
function drawCritters(t) {
  ctx.fillStyle = '#d8d0c0';
  MOTHS.forEach(([cx, cy, rx, ry, per], i) => { const a = TAU * (t / per + i * 0.29); ctx.globalAlpha = 0.8; ctx.fillRect(Math.round(cx + rx * Math.cos(a)), Math.round(cy + ry * Math.sin(a * 2 + i)), 1, 1); });
  ctx.globalAlpha = 1;
  FIREFLIES.forEach(([bx, by], i) => {
    const b = Math.pow(Math.max(0, wave(t, 24 + i * 2, i * 0.37)), 4);
    if (b < 0.05) return;
    const x = Math.round(bx + 8 * wave(t, 5 + i, i * 0.21)), y = Math.round(by + 5 * wave(t, 7 + i, i * 0.53));
    ctx.globalAlpha = b; ctx.fillStyle = '#e8ff9a'; ctx.fillRect(x, y, 1, 1); ctx.globalAlpha = 1;
    halo(HL.firefly, x, y, b);
  });
}
// low mist breathing along the far bank and over the river
const MISTS = [{ seed: 5, y: 157, h: 16, k: -1, ph: 0 }, { seed: 9, y: 178, h: 14, k: 2, ph: 0.4 }];
function renderMist(def) {
  const r = rng(def.seed), puffs = [];
  for (let i = 0; i < 9; i++) puffs.push({ x: r() * W, y: def.h * (0.4 + r() * 0.3), rx: 26 + r() * 40, ry: 2.5 + r() * 2.5 });
  const p = painter(W, def.h);
  for (let y = 0; y < def.h; y++) for (let x = 0; x < W; x++) {
    let best = 0;
    for (const q of puffs) { let dx = x - q.x; dx -= Math.round(dx / W) * W; best = Math.max(best, 1 - (dx * dx) / (q.rx * q.rx) - ((y - q.y) ** 2) / (q.ry * q.ry)); }
    if (best <= 0 || best * 1.3 < bayer(x, y)) continue;
    p.put(x, y, y < def.h * 0.45 ? '#7a84b0' : '#5c6690', Math.round(40 + 70 * Math.min(1, best)));
  }
  return p.done();
}
function drawScroll(cv, y, k, t, a = 1) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.globalAlpha = a; ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); ctx.globalAlpha = 1;
}
// the wisp that veils the moon before each chamki
function drawMoonWisp(t) {
  for (const [a, b] of MOON_WISP) if (t >= a && t < b) {
    const u = (t - a) / (b - a);
    ctx.globalAlpha = env(t, a, b, 3);
    ctx.drawImage(MOONWISP_C, Math.round(MOON.x - 34 + lerp(-64, 64, u)), MOON.y - 6);
    ctx.globalAlpha = 1;
  }
}

/* ================= Weather picks ================= */
const WEATHER = weatherPlans(['blossoms']);
const WX_LAND = [[150, SHORE + 2], [196, 232], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: LANTERN.x, y: LANTERN.y + 6, rx: 26, ry: 24, col: '#ffd890' }, { x: 53, y: 186, rx: 30, ry: 24, col: '#ffcf88' },
  { x: 18, y: 175, rx: 16, ry: 14, col: '#ffcf88' }, { x: 12, y: 124, rx: 12, ry: 12, col: '#ffc878' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB_LAMP.x, y: BB_LAMP.y + 6, rx: 18, ry: 12, col: '#ffe2a8' }] };

/* ================= Pre-render everything static ================= */
let HL, PUFF, SKY_C, MOON_C, WISP_C, MOONWISP_C, MIST_C, BACK_C, BACK_DIM, WATER_C, HOUSE_C, NEAR, LEDGE_C, BB_AD, BB_FRAME, PITCH, VIG_C;
function renderBack() {
  const p = painter(W, H), put = p.put;
  drawFarTrees(put);
  drawTerrace(put);
  drawTower(put, TOWER_L); drawTower(put, TOWER_X);
  drawMosque(put, 2 * CX - MOSQ.x0 - MOSQ.w + 1, true);
  drawMosque(put, MOSQ.x0, false);
  drawTaj(put);
  return p.done();
}
function renderDim(src) {                                         // the far bank under the veiled moon
  const [c, x] = makeCanvas(W, H);
  x.drawImage(src, 0, 0);
  x.globalCompositeOperation = 'source-atop'; x.fillStyle = 'rgba(10,12,34,0.5)'; x.fillRect(0, 0, W, H);
  return c;
}
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    const a = clamp((d - 0.7) * 1.4, 0, 0.55);
    if (a > bayer(x, y) * 0.6 + 0.05) p.put(x, y, '#03050e', Math.round(a * 200));
  }
  return p.done();
}
function prerender() {
  HL = {
    moon: makeHalo(70, '#8ea4e8', 0.2, 1.8), moonCore: makeHalo(20, '#eef0ff', 0.32, 1.5), taj: makeHalo(80, '#b8bcf0', 0.07, 1.6),
    tajFront: makeHalo(64, '#d8d8ff', 0.12, 1.4),
    win: makeHalo(18, '#ffb860', 0.2), door: makeHalo(30, '#ffb860', 0.2, 1.6), tube: makeHalo(12, '#d8ecff', 0.22, 1.4),
    diya: makeHalo(5, '#ffc060', 0.5, 1.3), diyaGlow: makeHalo(16, '#ffa850', 0.16, 1.5), lantern: makeHalo(28, '#ffc870', 0.22, 1.6),
    lanternCore: makeHalo(7, '#ffe8b8', 0.5, 1.3), boat: makeHalo(6, '#ffc870', 0.4, 1.3), bulb: makeHalo(4, '#ffc070', 0.35, 1.2),
    bbLamp: makeHalo(6, '#ffd890', 0.45, 1.4), bbWash: makeHalo(26, '#ffc070', 0.12, 1.5), firefly: makeHalo(4, '#c8ff6a', 0.3, 1.3), diyaPool: makeHalo(12, '#ffa850', 0.3, 1.3),
  };
  PUFF = [null]; for (let r = 1; r <= 8; r++) PUFF.push(makePuff(r, '#77758c', '#55546a'));
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp);
  MOONWISP_C = renderWisp({ seed: 41, y: 0, h: 12, len: 52, k: 0, x: 8 });
  MIST_C = MISTS.map(renderMist);
  BACK_C = renderBack(); BACK_DIM = renderDim(BACK_C); WATER_C = renderWater();
  const hp = renderHouse(); renderGhat(hp); HOUSE_C = hp.done();
  NEAR = renderNearRight(); LEDGE_C = renderLedge();
  BB_AD = renderBillboardAd(); BB_FRAME = renderBillboardFrame();
  PITCH = makePitch(BB.w, BB.h, { accent: '#e8d9ff' });
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
const CROWD = 0.6;
function renderFrame(t, { pitch = false, billboard = true, weather, crowd = CROWD, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, vl = veil(t), ch = chamki(t), bob = Math.round(0.6 * wave(t, 30, 0.2));
  // sky, stars, the moon, the wisps, the bats crossing the moon
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e6e8ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 1 - 0.55 * vl); halo(HL.moonCore, MOON.x, MOON.y, 1 - 0.6 * vl);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  WISPS.forEach((w, i) => drawScroll(WISP_C[i], w.y, w.k, t));
  drawMoonWisp(t);
  drawMoonBats(t);
  halo(HL.taj, CX, 92, 0.9 + 0.5 * ch - 0.4 * vl);
  if (wx.blossoms) drawFlowers(0, t);
  // the far bank: trees, the terrace, the towers, the jawab, the mosque and the Taj (dimmed under the veiled moon)
  ctx.drawImage(BACK_C, 0, 0);
  if (vl > 0.01) { ctx.globalAlpha = 0.55 * vl; ctx.drawImage(BACK_DIM, 0, 0); ctx.globalAlpha = 1; }
  if (ch > 0.01) halo(HL.tajFront, CX, 96, ch);
  drawGlints(t);
  drawDomeBats(t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the Yamuna: its own colour, the soft mirror, the lap at the wall, the moon's path, ripples, the mist
  ctx.drawImage(WATER_C, 0, SHORE);
  reflect(ctx, t, { top: SHORE, rows: H - SHORE, squash: 1.45, alpha: 0.42, amp: 0.75, grow: 0.035, k1: 30, k2: 72 });
  ctx.fillStyle = 'rgba(8,14,36,0.28)'; ctx.fillRect(0, SHORE, W, H - SHORE);
  drawLap(t);
  streak(ctx, t, MOON.x, 7, '#f0ecdc', 0.6 * (1 - 0.6 * vl), 3, SHORE + 1, SHORE + 52);
  streak(ctx, t, MOON.x, 11, '#c8c8e0', 0.35 * (1 - 0.6 * vl), 7, SHORE + 30, H);
  drawRipples(t);
  drawGlitter(t, vl);
  MISTS.forEach((m, i) => drawScroll(MIST_C[i], m.y, m.k, t, 0.75 + 0.25 * wave(t, 3, m.ph)));
  drawSplashes(t);
  drawEgrets(t);
  drawRowboat(t, crowd);
  streak(ctx, t, LANTERN.x, 4, '#ffcf88', 0.45, 11, JETTY.y + 3, H);
  // the near bank: the house and its ghat, the jetty, the moored boat and the tree
  ctx.drawImage(HOUSE_C, 0, 0);
  halo(HL.win, 12, 124, 0.9); halo(HL.win, 18, 175, 1); halo(HL.door, 53, 184, 1); halo(HL.tube, 35, 160, 0.9);
  drawHouseLife(t);
  drawSmoke(t);
  drawCaustics(t);
  drawBulbs(t);
  for (const dx of [89, 100]) { px(ctx, dx, 195, FLAME[(step(t, 1440) + dx) % 3]); halo(HL.diya, dx, 195, 0.8 + 0.2 * wave(t, 300, dx * 0.1)); }
  px(ctx, 68, 181, '#ffe0a0'); px(ctx, 68, 180, '#ff9a3a'); halo(HL.diya, 68, 180, 0.9 + 0.1 * wave(t, 280, 0.6));   // the diya in the wall niche by the door
  if (billboard) drawBillboard(t, pitch);
  ctx.drawImage(NEAR.right, 0, 0);
  ctx.drawImage(NEAR.crownA, Math.round(1.2 * wave(t, 20, 0.1)), 0);
  ctx.drawImage(NEAR.crownB, Math.round(1.2 * wave(t, 20, 0.45)), Math.round(0.6 * wave(t, 40, 0.3)));
  ctx.drawImage(NEAR.boat, 0, bob);
  const lf = 0.9 + 0.1 * hash(2, step(t, 1200), 77);
  ctx.fillStyle = '#fff2c8'; ctx.fillRect(LANTERN.x, LANTERN.y, 1, 2);
  halo(HL.lantern, LANTERN.x, LANTERN.y + 1, lf); halo(HL.lanternCore, LANTERN.x, LANTERN.y + 1, lf);
  drawCritters(t);
  if (wx.blossoms) drawFlowers(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawPeople(t, crowd, bob);
  // the near water mirrors the lit house and the moored boat; the floating diyas; the ledge the viewer leans on
  reflect(ctx, t, { top: NEAR_WL, rows: H - NEAR_WL, squash: 1.25, alpha: 0.36, amp: 0.5, grow: 0.04, k1: 40, k2: 90, x: 0, w: 152 });
  reflect(ctx, t, { top: 229, rows: H - 229, squash: 1.25, alpha: 0.3, amp: 0.5, grow: 0.04, k1: 40, k2: 90, x: 300, w: 180 });
  streak(ctx, t, 53, 6, '#ffcf88', 0.32, 17, NEAR_WL + 1, H);
  streak(ctx, t, 18, 5, '#ffcf88', 0.26, 19, NEAR_WL + 1, H);
  drawFloats(t);
  ctx.drawImage(LEDGE_C, 0, 0);
  drawDiyas(t);
  if (wx.blossoms) drawFlowers(2, t);
  ctx.drawImage(VIG_C, 0, 0);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
}

export default {
  id: 'agra',
  name: 'Agra',
  tagline: 'Moonlit Taj across the Yamuna',
  timeZone: 'Asia/Kolkata',
  accent: '#e8d9ff',
  focusX: CX,
  landmark: [MIN_F[0] - 6, MIN_F[1] + 6],                 // the Taj from minaret to minaret
  landmarkTop: TIP,                                       // the finial's crescent, which landscape crops keep
  billboard: BB,
  crowd: CROWD,
  ambience: { water: 0.45, crickets: 0.5, wind: 0.12, crowd: 0.04 },
  events: [
    ...SPLASH.map(s => ({ t: +(s.t + 0.5).toFixed(1), type: 'splash', x: s.x, gain: 0.3 })),
    { t: 57, type: 'chime', x: CX, gain: 0.35 }, { t: 177, type: 'chime', x: CX, gain: 0.35 },
    { t: CROSS.t0, type: 'boat', dur: CROSS.t1 - CROSS.t0, pan: [0.95, -1], gain: 0.3 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
